/*#
# schema-form

<!--{ "parent": "Components" }-->

A form generated from a [JSON Schema](https://json-schema.org/), with validation.

```js
import { tosiSchemaForm } from 'tosijs-ui'

const schemaForm = tosiSchemaForm({
  schema: {
    type: 'object',
    properties: {
      name: { type: 'string', title: 'Full name' },
      email: { type: 'string', format: 'email' },
      age: { type: 'integer' },
      role: { type: 'string', enum: ['admin', 'editor', 'viewer'] },
      active: { type: 'boolean' },
    },
    required: ['name', 'email'],
  },
  value: { name: 'Ada', email: 'ada@example.com', role: 'admin', active: true },
})

const shown = document.createElement('pre')
schemaForm.addEventListener('change', () => {
  shown.textContent = JSON.stringify(schemaForm.value, null, 2)
})
shown.textContent = JSON.stringify(schemaForm.value, null, 2)

preview.append(schemaForm, shown)
```

## The model owns the data

`value` is the state and `change` fires when it changes — the same contract as every other
component here. The inputs are a **view** of `value`; they are never the source of truth.

That is not a stylistic preference. Reading data back out of rendered inputs, as most
schema-form libraries do, means a field your schema does not describe is **dropped on save** —
so editing one field can discard timestamps, provenance, or anything else added since the
schema was written. Here `value` is a plain object that is copied, never rebuilt:

```typescript
schemaForm.value = { name: 'Ada', _id: 'abc', updatedAt: '2020-01-01' }
// edit `name` in the UI …
schemaForm.value // → { name: 'Grace', _id: 'abc', updatedAt: '2020-01-01' }
```

Editing also never mutates the object you handed in — you get a new one, so
`diff(original, schemaForm.value)` is a usable dirty check.

## Validation

Errors come from [`tosijs-schema`](https://www.npmjs.com/package/tosijs-schema) and appear
under the field they belong to. `validate()` returns whether the current value conforms, and
`errors` is the list.

```js
import { tosiSchemaForm } from 'tosijs-ui'

const schemaForm = tosiSchemaForm({
  schema: {
    type: 'object',
    properties: {
      name: { type: 'string' },
      email: { type: 'string', format: 'email' },
    },
    required: ['name', 'email'],
  },
  value: { name: 'Ada', email: 'not-an-email' },
})

const report = document.createElement('pre')
const update = () => {
  report.textContent = schemaForm.validate()
    ? 'conforms ✅'
    : schemaForm.errors.map((e) => `${e.path || '(form)'}: ${e.message}`).join('\n')
}
schemaForm.addEventListener('change', update)
update()

preview.append(schemaForm, report)
```

### Give it a validator

The form does not import a validation library. It asks for two functions, and you supply
them once, anywhere before or after the form renders:

```typescript
import { setSchemaValidator } from 'tosijs-ui'
import { validate, inferSchema, unenforcedKeywords } from 'tosijs-schema' // ^1.8.0

setSchemaValidator({ validate, inferSchema, unenforcedKeywords })
```

Pass **all three**. `unenforcedKeywords` is what lets the form ask the validator *"which
keywords do you not check?"* — omit it and the form falls back to a built-in list frozen at
tosijs-schema 1.7.0, so every `oneOf` and `exclusiveMinimum` field is labelled "not validated"
while the validator is checking it. The note exists to stop the form lying; the two-argument
recipe makes it the thing lying.

**Already done for you** in the CDN `<script>` build and in a `tosijs-ui/site` doc site —
those are bundles we build, so they register it themselves. Only an ESM consumer writes the
line.

Without a validator **and with a `schema`**, the form still renders and edits; it simply
reports no errors, warns once in the console, and `validationAvailable` reads `false` — so a
Save handler can tell *"this conforms"* from *"nobody checked"*, which `validate() === true`
alone cannot.

Without a validator **and without a `schema`** there is nothing to infer fields from, so the
form has none. It says that on screen rather than rendering an empty box, and the console
warning names *that* problem rather than the validation one — a message about error reporting
is the wrong thing to read while looking at a blank form.

Why a seam rather than an import: a bare `import('tosijs-schema')` in shipped code is either
resolved by your bundler — which **fails the build** for anyone who did not install it,
including people using only `<tosi-table>` — or left external, which cannot resolve in a
browser and kills validation for everyone. There is no third option, so the component asks
for the functions instead of the package. The upside is that they are just functions: an Ajv
wrapper, a house validator or a test stub all work. `tosijs-schema` is the one we ship docs
for, not a requirement.

## Non-conforming data

The form is an **editor, not a gate**. Give it a value that violates its schema and it renders
that value, validates on load, and marks the offending fields. It does not coerce the data,
strip it, refuse it, or stop you saving it.

Enforcement is yours, deliberately. The form has no validator of its own, so a save gate built
in here would pass silently for every consumer who never registered one — a gate that opens
when nothing checked is worse than no gate, because it reads as enforcement. `validate()` and
`validationAvailable` are the seam instead, and they are three lines in your save handler:

```typescript
if (form.validationAvailable && !form.validate()) {
  // your call: refuse, warn, save a draft
}
```

**Known limitation, tracked as
[#162](https://github.com/tonioloewald/tosijs-ui/issues/162).** In three cases the offending
value is kept in `value` but cannot be seen on screen:

- **A value the typed control cannot hold shows as empty.** `age: "abc"` against
  `{ type: 'integer' }` renders an empty `number` input, because the browser discards a value
  it cannot parse; an enum value not in the list leaves the `select` blank. `value.age` is
  still `"abc"` — but the field looks unset, and typing into it overwrites something you were
  never shown.
- **A union value matching no branch renders no fields** for that subtree. It survives in
  `value` and is uneditable.
- **An error whose path has no rendered field is invisible** — an extra property, or an error
  against the object as a whole. It is in `errors`, so `validate()` returns `false` over a form
  that looks clean.

The fix being built for the first two is a panel listing what could not be displayed, with the
option to purge it. Until then, treat `errors` as the authority on what is wrong and the fields
as a partial view of it.

## No schema? It infers one

Give it a `value` and no `schema` and it derives one with `inferSchema` from
the registered validator's `inferSchema` — useful for editing a record whose shape you don't
have written down. With no validator there is nothing to infer from, and the form says so
rather than rendering an empty box.

```js
import { tosiSchemaForm } from 'tosijs-ui'

const inferred = tosiSchemaForm({
  value: {
    title: 'Dorothea',
    pages: 812,
    inPrint: true,
    author: { surname: 'Eliot' },
  },
})
const shown = document.createElement('pre')
const show = () => {
  shown.textContent = JSON.stringify(inferred.schema, null, 2)
}
inferred.addEventListener('change', show)
show()
preview.append(inferred, shown)
```
```test
const inferred = await waitFor('tosi-schema-form')

test('a schema is derived from the value, and it is the one you can read back', async () => {
  // Inference needs a registered validator that can do it. On this doc site the iife
  // registers one, so the fields arrive on a render rather than after a module load — there
  // is no lazy import any more, the seam replaced it.
  const pages = await waitFor('[data-path="pages"]', 5000)

  // Types come from the data: a whole number is an integer, a nested object is a section.
  expect(pages.type).toBe('number')
  expect(inferred.querySelector('[data-path="inPrint"]').type).toBe('checkbox')
  expect(inferred.querySelector('[data-path="author.surname"]')).toBeTruthy()

  // `$inferred` marks it as OBSERVED rather than authored, and nothing is required —
  // one sample is evidence of what was there, not of what must be.
  expect(inferred.schema.$inferred).toBe(true)
  expect(inferred.schema.required).toBeUndefined()

  // Inference happens ONCE. Editing must not re-derive the schema and rebuild the form
  // out from under whoever is typing.
  const built = inferred.schema
  pages.value = '900'
  pages.dispatchEvent(new Event('input', { bubbles: true }))
  expect(inferred.value.pages).toBe(900)
  expect(inferred.schema).toBe(built)
})
```

An inferred schema describes **the sample you gave it** — nothing more. It carries
`$inferred: true` so you can tell it from one you wrote, it is open
(`additionalProperties: true`) so `filter()` cannot strip a key that happened to be absent,
and it has **no `required`**: asserting that a field is mandatory because one example filled
it in is exactly the guess that makes inferred schemas reject valid future data.

It is inferred **once**, so typing never rebuilds the form. Read it back from `.schema`, edit
it, and set it again if you want something different — that round trip is the point.

## Nested objects

An object property becomes a `<details>` section, to any depth. Sections start **open** — a
form whose fields are hidden looks empty, and a user who does not know a section exists cannot
fill it in.

`required` is scoped to the object that declares it, which is what JSON Schema means: a
required `city` inside an optional `address` says *if you give an address, it needs a city*.

```js
import { tosiSchemaForm } from 'tosijs-ui'

preview.append(
  tosiSchemaForm({
    schema: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        address: {
          type: 'object',
          title: 'Postal address',
          properties: {
            city: { type: 'string' },
            postcode: { type: 'string' },
            geo: {
              type: 'object',
              properties: {
                lat: { type: 'number' },
                lon: { type: 'number' },
              },
            },
          },
          required: ['city'],
        },
      },
      required: ['name'],
    },
    value: { name: 'Ada', address: { city: 'London', geo: { lat: 51.5 } } },
  })
)
```
```test
const nestedForm = await waitFor('tosi-schema-form')

test('nested objects render as sections, with fully-qualified paths', () => {
  expect(nestedForm.querySelectorAll('details.schema-group').length).toBe(2)
  expect(nestedForm.querySelector('[data-path="address.geo.lat"]')).toBeTruthy()
  // Sections start open, or the form reads as empty.
  expect([...nestedForm.querySelectorAll('details')].every((d) => d.open)).toBe(true)
})

test('editing a nested field writes the nested path and keeps its siblings', () => {
  const lat = nestedForm.querySelector('[data-path="address.geo.lat"]')
  lat.value = '48.9'
  lat.dispatchEvent(new Event('input', { bubbles: true }))
  expect(nestedForm.value.address.geo.lat).toBe(48.9)
  expect(nestedForm.value.address.city).toBe('London')
  expect(nestedForm.value.name).toBe('Ada')
})
```

## Arrays

An array property renders its elements with add, remove and reorder controls. Items can be
scalars or objects, and objects can contain further arrays.

Editing an array **splices the model**. That is worth stating because the usual approach —
rewriting the DOM path strings of every following element — is where these components
famously corrupt data: reindexing a nested array with an unanchored pattern rewrites the
*outer* index, so moving `items[2].variants[1]` puts its data on `items[0]`. There are no
path strings here, so there is nothing to rewrite wrongly. The indices are wherever the
elements now are.

```js
import { tosiSchemaForm } from 'tosijs-ui'

preview.append(
  tosiSchemaForm({
    schema: {
      type: 'object',
      properties: {
        tags: { type: 'array', title: 'Tag', items: { type: 'string' } },
        items: {
          type: 'array',
          title: 'Line item',
          items: {
            type: 'object',
            properties: {
              sku: { type: 'string' },
              qty: { type: 'integer' },
            },
            required: ['sku'],
          },
        },
      },
    },
    value: {
      tags: ['urgent', 'paid'],
      items: [
        { sku: 'WIDGET-1', qty: 2 },
        { sku: 'GASKET-9', qty: 5 },
      ],
    },
  })
)
```
```test
const arrayForm = await waitFor('tosi-schema-form')
const skus = () => arrayForm.value.items.map((i) => i.sku)

test('array elements render with their own paths', () => {
  expect(arrayForm.querySelector('[data-path="tags.0"]').value).toBe('urgent')
  expect(arrayForm.querySelector('[data-path="items.1.sku"]').value).toBe('GASKET-9')
})

test('add, reorder and remove all edit the model', () => {
  // One test: these steps share the form, and the live-example docs are explicit that
  // test() bodies run concurrently.
  const container = arrayForm.querySelector('[data-array="items"]')

  container.querySelector('.schema-add').click()
  expect(arrayForm.value.items.length).toBe(3)

  // Reorder the first two.
  const controls = container.querySelectorAll('.schema-item-controls')
  controls[1].querySelector('.schema-move-up').click()
  expect(skus().slice(0, 2)).toEqual(['GASKET-9', 'WIDGET-1'])

  // Remove the one we added.
  const after = arrayForm.querySelectorAll('[data-array="items"] .schema-item')
  after[2].querySelector('.schema-remove').click()
  expect(arrayForm.value.items.length).toBe(2)
  expect(skus()).toEqual(['GASKET-9', 'WIDGET-1'])
})

test('an array edit does not disturb the rest of the form', () => {
  // Only the edited array is rebuilt. A form that rebuilt everything would throw away focus,
  // scroll and every open section elsewhere on the page.
  const tag = arrayForm.querySelector('[data-path="tags.0"]')
  arrayForm.querySelector('[data-array="items"] .schema-add').click()
  expect(arrayForm.querySelector('[data-path="tags.0"]')).toBe(tag)
  expect(arrayForm.value.tags).toEqual(['urgent', 'paid'])
})
```

## Unions

`anyOf` and `oneOf` describe several shapes a value may take, and most of them are not
"pick a variant" at all. Each is rendered as what it actually is:

| the union | what you get |
| --- | --- |
| `[X, {type: 'null'}]` | just an X, not required — this is what an optional field looks like |
| all branches `const` | a `<select>`; a branch `title` is its label |
| all branches objects | a variant picker plus the fields of the matching branch |
| anything else | a placeholder naming the shapes, e.g. *a union of string \| object* |

A variant union finds its **discriminator** — the property every branch pins to a different
`const` — and uses it both to tell which branch the value matches and to label the choices.
An OpenAPI-style `discriminator: {propertyName}` is honoured when present, but plain JSON
Schema needs no extra ceremony. The discriminator is **not** rendered as a field of its own:
the picker is that control, and two controls for one value is an invitation to set them
differently.

Switching branch writes the new branch's `const` marks and **deletes nothing** — switch back
and your data is still there. `filter()` is what strips a value to a schema; a form is an
editor.

```js
import { tosiSchemaForm } from 'tosijs-ui'

const unionForm = tosiSchemaForm({
  schema: {
    type: 'object',
    properties: {
      nickname: { anyOf: [{ type: 'string' }, { type: 'null' }] },
      notify: {
        title: 'Notify',
        anyOf: [
          { const: 'all', title: 'Everything' },
          { const: 'mentions', title: 'Mentions only' },
          { const: 'none', title: 'Nothing' },
        ],
      },
      shape: {
        anyOf: [
          {
            type: 'object',
            title: 'Circle',
            properties: { kind: { const: 'circle' }, r: { type: 'number' } },
            required: ['kind', 'r'],
          },
          {
            type: 'object',
            title: 'Rectangle',
            properties: {
              kind: { const: 'rect' },
              w: { type: 'number' },
              h: { type: 'number' },
            },
            required: ['kind', 'w', 'h'],
          },
        ],
      },
    },
    required: ['nickname'],
  },
  value: { notify: 'mentions', shape: { kind: 'circle', r: 3 } },
})

const shown = document.createElement('pre')
const show = () => {
  shown.textContent = JSON.stringify(unionForm.value, null, 2)
}
unionForm.addEventListener('change', show)
show()

preview.append(unionForm, shown)
```
```test
const unionForm = await waitFor('tosi-schema-form')
const picker = unionForm.querySelector('[data-union="shape"] .schema-variant')

test('unions render as what they actually are', async () => {
  // ONE test: every step below shares this form, and test() bodies run concurrently.

  // A nullable union is just the field — and `required` in the schema does not make an
  // empty value invalid when null is a branch.
  expect(unionForm.querySelector('[data-path="nickname"]').type).toBe('text')

  // An all-const union is a select, labelled by branch titles.
  const notify = unionForm.querySelector('[data-path="notify"]')
  expect(notify.tagName).toBe('SELECT')
  // `notify` is not required, so the first option is the empty one — an optional field has
  // to offer a way back to "not set".
  expect([...notify.options].map((o) => o.textContent)).toEqual([
    '—',
    'Everything',
    'Mentions only',
    'Nothing',
  ])
  expect(notify.value).toBe('mentions')

  // The variant picker shows the matching branch's fields — and NOT the discriminator,
  // which the picker itself is.
  expect(picker.value).toBe('0')
  expect(unionForm.querySelector('[data-path="shape.r"]').value).toBe('3')
  expect(unionForm.querySelector('[data-path="shape.kind"]')).toBe(null)

  // Switching branch swaps the fields, writes the new mark, and keeps the old data.
  picker.value = '1'
  picker.dispatchEvent(new Event('change', { bubbles: true }))
  expect(unionForm.value.shape.kind).toBe('rect')
  expect(unionForm.value.shape.r).toBe(3)
  expect(unionForm.querySelector('[data-path="shape.w"]')).toBeTruthy()
  expect(unionForm.querySelector('[data-path="shape.r"]')).toBe(null)

  // Setting `value` to a different variant re-renders the branch, without a schema change.
  unionForm.value = { shape: { kind: 'circle', r: 9 } }
  await new Promise((r) => requestAnimationFrame(r))
  expect(picker.value).toBe('0')
  expect(unionForm.querySelector('[data-path="shape.r"]').value).toBe('9')

  // Through all of that the picker itself was never replaced — switching variant with the
  // keyboard must not take focus off the control you are operating.
  expect(unionForm.querySelector('[data-union="shape"] .schema-variant')).toBe(
    picker
  )
})
```

### When a keyword is not validated, the field says so

A validator enforces a subset of JSON Schema, and the parts outside it are the dangerous ones:
not an error, not a warning — a value the schema forbids, accepted, with `ok === true`. So any
field whose schema uses a keyword the **registered validator** does not check carries a note
saying which.

The form asks the validator itself, whenever it can answer (`unenforcedKeywords`, which
`tosijs-schema` exports from 1.8.0 — it was ask 3 of
[#8](https://github.com/tonioloewald/tosijs-schema/issues/8), filed from here). Only a
validator that cannot answer falls back to a local list, because the validator in use is the
only thing that actually knows.

That distinction is not academic: `tosijs-schema` **used to ignore `oneOf`** — `validate`
returned `true` for a value no branch accepted — and 1.8.0 enforces it. A hard-coded list
would now be claiming "oneOf is not validated" over a field being checked, which is the note
lying in the component whose whole point is that it does not.

> **Prefer `anyOf` for a discriminated union.** 1.8.0 validates `oneOf` by trying every branch
> with no short-circuit, where `anyOf` stops at the first match. Same result, more work.

```js
import { tosiSchemaForm } from 'tosijs-ui'

preview.append(
  tosiSchemaForm({
    schema: {
      type: 'object',
      properties: {
        score: { type: 'number', exclusiveMinimum: 0 },
        tags: { type: 'array', items: { type: 'string' }, uniqueItems: true },
        shape: {
          oneOf: [
            {
              type: 'object',
              title: 'Circle',
              properties: { kind: { const: 'circle' }, r: { type: 'number' } },
            },
            {
              type: 'object',
              title: 'Square',
              properties: { kind: { const: 'square' }, side: { type: 'number' } },
            },
          ],
        },
      },
    },
    value: { score: 0, tags: ['a', 'a'], shape: { kind: 'square', side: 2 } },
  })
)
```
```test
const oneOfForm = await waitFor('tosi-schema-form')

test('the note tracks the VALIDATOR, not a hard-coded list', () => {
  expect(oneOfForm.querySelector('[data-path="shape.side"]').value).toBe('2')

  const notes = [...oneOfForm.querySelectorAll('.schema-unvalidated')].map(
    (n) => n.textContent
  )
  // Against tosijs-schema >= 1.8.0 both `oneOf` and `exclusiveMinimum` ARE enforced, so
  // there is nothing to warn about and `score: 0` is correctly rejected. Under 1.7.0 this
  // same form rendered two notes and reported the value as conforming — the assertion that
  // matters is that the notes agree with the validator in use, whichever it is.
  // (Line comments: a block comment's `*` `/` closes the doc block early — see #70.)
  // `uniqueItems` is on an ARRAY and is still not enforced by 1.8.0, so its note is the one
  // that must appear — containers carried none until 1.11.0, which made the promise above
  // false for exactly the keywords a validator is most likely to skip.
  expect(notes).toEqual(['uniqueItems is not validated'])
  expect(oneOfForm.validate()).toBe(false)
  expect(oneOfForm.errors.some((e) => e.path === 'score')).toBe(true)
})
```

## Format plugins

`registerFieldPlugin(format, plugin)` claims a schema `format` and renders it yourself. The
plugin owns the **control**; the form still owns the label and the error slot, so a plugin
does not have to re-implement either and cannot drift from the rest of the form.

```typescript
registerFieldPlugin('percent', {
  // build the control element
  render({ value, set, elements }) { return elements.div() },
  // put a value back into it
  sync(element, value) {},
  // injected into its own stylesheet when the plugin registers
  styles: { '.my-widget': { display: 'flex' } },
})
```

`sync` is **required**. A control that cannot take a value back is not compatible with a form
whose model owns the data, and finding that out while writing the plugin beats finding it out
when a save silently reverts. The form calls it whenever `value` changes underneath — never by
replacing your element, which would ruin a slider mid-drag.

**Register whenever you like — there is no "too late".** Styles are injected when the plugin
registers, into their own stylesheet, and any form on screen that uses the format rebuilds.
(Forms that do not use it are left alone.) The component this design learned from merged
plugin styles into its style spec *once, when the component was defined*, so a plugin
registered a moment later rendered correctly and completely unstyled — with no error. Its
workaround was a bare `import './plugin'` placed above the component definition: load-bearing,
invisible, and unavailable to anyone registering from application code.

```js
import { tosiSchemaForm, registerFieldPlugin } from 'tosijs-ui'

const pluginForm = tosiSchemaForm({
  schema: {
    type: 'object',
    properties: {
      label: { type: 'string' },
      confidence: { type: 'number', format: 'percent', title: 'Confidence' },
    },
  },
  value: { label: 'match', confidence: 0.85 },
})
const shown = document.createElement('pre')
const show = () => {
  shown.textContent = JSON.stringify(pluginForm.value)
}
pluginForm.addEventListener('change', show)
show()
preview.append(pluginForm, shown)

// Let the form render FIRST, with no plugin for `percent` — otherwise this example would
// quietly be testing early registration, which was never the hard case.
await new Promise((r) => requestAnimationFrame(r))

// Registered AFTER the form is already on screen — which is the normal case, and works.
registerFieldPlugin('percent', {
  render({ value, set, elements: { div, input, span } }) {
    const readout = span({ class: 'percent-readout' })
    const slider = input({
      type: 'range',
      min: 0,
      max: 1,
      step: 0.01,
      onInput(event) {
        const next = Number(event.target.value)
        readout.textContent = `${Math.round(next * 100)}%`
        set(next)
      },
    })
    const control = div({ class: 'percent-control' }, slider, readout)
    slider.value = String(value ?? 0)
    readout.textContent = `${Math.round((value ?? 0) * 100)}%`
    return control
  },
  sync(element, value) {
    const slider = element.querySelector('input')
    slider.value = String(value ?? 0)
    element.querySelector('.percent-readout').textContent = `${Math.round(
      (value ?? 0) * 100
    )}%`
  },
  styles: {
    '.percent-control': { display: 'flex', gap: '8px', alignItems: 'center' },
    '.percent-readout': { minWidth: '3em', fontVariantNumeric: 'tabular-nums' },
  },
})
```
```test
const pluginForm = await waitFor('tosi-schema-form')

test('a plugin registered after the form renders takes effect, styles and all', async () => {
  // The rebuild is queued for a frame, and under load one rAF is not reliably that frame —
  // poll instead of racing it.
  const control = await waitFor('[data-plugin="confidence"]')
  expect(control.querySelector('input').type).toBe('range')
  expect(control.querySelector('.percent-readout').textContent).toBe('85%')

  // The styles went in on registration, not when the component was defined — the whole
  // point. A silently unstyled plugin is the defect this seam exists to make impossible.
  expect(getComputedStyle(control).display).toBe('flex')

  // Dragging the slider writes the MODEL, through the plugin's `set`.
  const slider = control.querySelector('input')
  slider.value = '0.4'
  slider.dispatchEvent(new Event('input', { bubbles: true }))
  expect(pluginForm.value.confidence).toBe(0.4)
  // …and the field the plugin does not own is untouched.
  expect(pluginForm.value.label).toBe('match')

  // Setting `value` syncs THROUGH the plugin rather than replacing its element.
  pluginForm.value = { label: 'match', confidence: 0.6 }
  await new Promise((r) => requestAnimationFrame(r))
  expect(pluginForm.querySelector('[data-plugin="confidence"]')).toBe(control)
  expect(control.querySelector('.percent-readout').textContent).toBe('60%')
})
```

## Curated forms

A schema-form shows every property the schema describes. That is right for an admin view and
wrong for a task-focused one: a panel with four fields from a two-hundred-property
configuration, an onboarding step, a review dialog.

Set **`fields`** to choose what is shown. It is a map from **paths** into the schema to how
each one should appear, and its order is the display order:

```typescript
form.fields = {
  'thresholds.default': { caption: 'Default threshold', help: 'Between 0 and 1.' },
  'routing.mode': 'How invoices are routed', // a string is just a caption
  'routing.priority': { editor: tosiRating }, // a custom editor
  owner: { readOnly: true }, // shown, not editable here
  notify: true, // exactly as the schema describes it
}
```

- Everything else still comes from the schema: the control, `enum` choices, `required`,
  number and format constraints, and the caption when you give none.
- A path runs through object properties (`a.b.c`). Naming an object, an array or a union
  shows that whole property, unless its entry has its own `fields`
  ([below](#inside-arrays-and-unions)).
- **`editor`** is a function that returns an element with a `value` property and a `change`
  event, which is what every element creator in this library is. Its `value` must be of the
  schema's type: a native `<input>` yields strings, so use one for string properties only. Pass a [field plugin](#format-plugins) object (`{ render, sync }`) for full control.
- **`readOnly`** on one field is a presentation choice, not a security boundary: the value is
  still writable through `form.value`.
- Name an object or one of its properties, not both: the first one listed is shown and the
  console warns about the other.
- A path the schema does not describe renders as a placeholder that says so, and the console
  warns. A field that silently fails to appear looks the same as one nobody asked for.

**The form still owns the whole value.** Properties you do not show keep their values, and
the whole value is validated. An error in a property that is not shown is listed above the
fields, so a curated form cannot quietly save a value that does not conform. `errors` and
`validate()` always describe the whole value.

Set `fields = null` to show everything again.

Tick **View only** below to turn the same curated form into a viewer, and **Every field** to
see what the schema describes when nothing is curated.

```js
import { tosiSchemaForm, tosiRating } from 'tosijs-ui'
import { elements } from 'tosijs'

const { div, label, input, pre } = elements

const configSchema = {
  type: 'object',
  properties: {
    owner: { type: 'string' },
    thresholds: {
      type: 'object',
      properties: {
        default: { type: 'number', minimum: 0, maximum: 1 },
        escalate: { type: 'number', minimum: 0, maximum: 1 },
      },
      required: ['default'],
    },
    routing: {
      type: 'object',
      properties: {
        mode: { type: 'string', enum: ['manual', 'assisted', 'automatic'] },
        priority: { type: 'integer', minimum: 0, maximum: 5 },
        retries: { type: 'integer', minimum: 0 },
      },
    },
    audit: { type: 'object', properties: { retainDays: { type: 'integer' } } },
  },
}

const fields = {
  'thresholds.default': {
    caption: 'Default threshold',
    help: 'Predictions below this go to a person. Between 0 and 1.',
  },
  'routing.mode': 'How invoices are routed',
  'routing.priority': { caption: 'Priority', editor: tosiRating },
  owner: { caption: 'Owned by', readOnly: true },
}

const curatedForm = tosiSchemaForm({
  schema: configSchema,
  value: {
    owner: 'finance-ops',
    thresholds: { default: 0.8, escalate: 0.95 },
    routing: { mode: 'assisted', priority: 3, retries: 2 },
    audit: { retainDays: 90 },
  },
  fields,
})

const curatedValue = pre({ class: 'curated-value' })
const showCurated = () => {
  curatedValue.textContent = JSON.stringify(curatedForm.value, null, 2)
}
curatedForm.addEventListener('change', showCurated)
showCurated()

preview.append(
  div(
    { class: 'curated-options' },
    label(
      input({
        type: 'checkbox',
        class: 'view-only',
        onChange(event) {
          curatedForm.readOnly = event.target.checked
        },
      }),
      ' View only'
    ),
    label(
      input({
        type: 'checkbox',
        class: 'every-field',
        onChange(event) {
          curatedForm.fields = event.target.checked ? null : fields
        },
      }),
      ' Every field'
    )
  ),
  div({ class: 'curated-demo' }, curatedForm, curatedValue)
)
```
```css
.preview .curated-options {
  display: flex;
  gap: 16px;
  padding: 0 0 12px;
}
.preview .curated-demo {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
}
.preview .curated-demo tosi-schema-form {
  flex: 1 1 280px;
}
.preview .curated-value {
  flex: 0 1 240px;
  margin: 0;
  font-size: 12px;
}
```
```test
const curatedForm = await waitFor('tosi-schema-form')
await waitMs(100)

test('a curated form shows the chosen fields, in order, under the chosen captions', () => {
  const shown = [...curatedForm.querySelectorAll('[data-field]')]
  expect(shown.map((el) => el.dataset.field)).toEqual([
    'thresholds.default',
    'routing.mode',
    'routing.priority',
    'owner',
  ])
  expect(shown[0].querySelector('label').textContent).toBe('Default threshold')
  expect(shown[0].querySelector('.schema-help').textContent).toContain('Between 0 and 1')
  // the control still comes from the schema
  expect(curatedForm.querySelector('[data-path="thresholds.default"]').type).toBe('number')
  expect(curatedForm.querySelector('[data-path="routing.mode"]').tagName).toBe('SELECT')
  expect(curatedForm.querySelector('[data-path="owner"]').disabled).toBe(true)
  expect(curatedForm.querySelector('[data-path="routing.mode"]').disabled).toBe(false)
  // a conforming value shows no problems
  expect(curatedForm.querySelector('.schema-hidden-errors').hidden).toBe(true)
})

test('editing one field keeps everything that is not shown', () => {
  const mode = curatedForm.querySelector('[data-path="routing.mode"]')
  mode.value = 'automatic'
  mode.dispatchEvent(new Event('input', { bubbles: true }))
  expect(curatedForm.value.routing).toEqual({ mode: 'automatic', priority: 3, retries: 2 })
  expect(curatedForm.value.thresholds.escalate).toBe(0.95)
  expect(curatedForm.value.audit.retainDays).toBe(90)
  mode.value = 'assisted'
  mode.dispatchEvent(new Event('input', { bubbles: true }))
})

test('a custom editor reads and writes the value', () => {
  const rating = curatedForm.querySelector('[data-plugin="routing.priority"]')
  expect(rating.tagName).toBe('TOSI-RATING')
  expect(rating.value).toBe(3)
  let fired = 0
  const count = () => (fired += 1)
  curatedForm.addEventListener('change', count)
  rating.value = 5
  rating.dispatchEvent(new Event('change', { bubbles: true }))
  expect(curatedForm.value.routing.priority).toBe(5)
  expect(fired).toBe(1)
  rating.value = 3
  rating.dispatchEvent(new Event('change', { bubbles: true }))
  curatedForm.removeEventListener('change', count)
})

test('the same form is a viewer, and shows every field on request', async () => {
  const toggle = (selector, checked) => {
    const box = preview.querySelector(selector)
    box.checked = checked
    box.dispatchEvent(new Event('change', { bubbles: true }))
  }
  // the form re-renders on a frame, which a background page may delay
  const settled = async (check) => {
    for (let i = 0; i < 50 && !check(); i += 1) await waitMs(100)
    return check()
  }
  const disabled = (path) => curatedForm.querySelector(`[data-path="${path}"]`)?.disabled
  const shows = (path) => curatedForm.querySelector(`[data-path="${path}"]`) != null

  toggle('.view-only', true)
  expect(await settled(() => disabled('routing.mode') === true)).toBe(true)
  toggle('.view-only', false)
  expect(await settled(() => disabled('routing.mode') === false)).toBe(true)
  expect(disabled('owner')).toBe(true)

  toggle('.every-field', true)
  expect(await settled(() => shows('audit.retainDays'))).toBe(true)
  toggle('.every-field', false)
  expect(await settled(() => !shows('audit.retainDays'))).toBe(true)
  expect(curatedForm.querySelectorAll('[data-field]').length).toBe(4)
})
```

### A problem in a field that is not shown

This value arrives with an `audit.retainDays` that is not an integer. The form shows one
field, and still reports the problem:

```js
import { tosiSchemaForm } from 'tosijs-ui'

const hiddenErrorForm = tosiSchemaForm({
  schema: {
    type: 'object',
    properties: {
      owner: { type: 'string' },
      audit: { type: 'object', properties: { retainDays: { type: 'integer' } } },
    },
  },
  value: { owner: 'finance-ops', audit: { retainDays: 'ninety' } },
  fields: { owner: 'Owned by' },
})
hiddenErrorForm.validate()

preview.append(hiddenErrorForm)
```
```test
const hiddenErrorForm = await waitFor('tosi-schema-form')
await waitMs(100)

test('an error in a field that is not shown is listed, not hidden', () => {
  if (!hiddenErrorForm.validationAvailable) return
  expect(hiddenErrorForm.validate()).toBe(false)
  const summary = hiddenErrorForm.querySelector('.schema-hidden-errors')
  expect(summary.hidden).toBe(false)
  expect(summary.textContent).toContain('audit.retainDays')
})
```

### Inside arrays and unions

What is inside an array or a union depends on the value, so a path cannot run through one.
Give the property's entry its own **`fields`** instead. Its paths are relative: for an array,
to **each item**; for a union, to **the branch the value currently matches**.

```typescript
form.fields = {
  thresholds: {
    caption: 'Thresholds',
    fields: {
      value: 'Threshold', // thresholds[i].value, for every item
      'when.company': { caption: 'Company', readOnly: true },
    },
  },
  rule: {
    picker: false, // hide the variant picker
    fields: { amount: 'Amount', rate: 'Rate' }, // each branch shows the ones it has
  },
}
```

- Items keep the properties you do not show, and Add, remove and reorder work as before.
- An error in an item property that is not shown is listed above the fields by its real
  path (`thresholds.0.note`).
- A union's `fields` covers every branch. A path the active branch lacks is left out; a path
  **no** branch has renders the placeholder and warns.
- `fields` on an object entry works the same way, and keeps the object's panel.
- `collapsed: true` on an object or array entry starts its panel closed.

### Sections

An entry with a **`section`** title is a panel that groups some of the fields. Its key is an
id (the panel's `data-section`, for styling), not a path, and the paths in its `fields` are
the same paths you would use outside it.

```typescript
form.fields = {
  owner: 'Owned by',
  advanced: {
    section: 'Advanced',
    collapsed: true,
    fields: { 'routing.retries': 'Retries', 'audit.retainDays': 'Keep for (days)' },
  },
}
```

A section starts open unless you say `collapsed: true`. A section, and any entry with
`collapsed: true`, gets the class `-invalid` and a red title while a field inside it is in
error, so collapsing one cannot hide a problem. In TypeScript, `isSection(entry)` tells a
section from a field entry when you walk a curation yourself. Sections can sit inside an array's or a union's `fields` too.

```js
import { tosiSchemaForm } from 'tosijs-ui'
import { elements } from 'tosijs'

const { div, pre } = elements

const rulesForm = tosiSchemaForm({
  schema: {
    type: 'object',
    properties: {
      owner: { type: 'string' },
      thresholds: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            when: {
              type: 'object',
              properties: {
                company: { type: 'string' },
                account: { type: 'string' },
              },
            },
            value: { type: 'number', minimum: 0, maximum: 1 },
            note: { type: 'string' },
          },
        },
      },
      routing: {
        type: 'object',
        properties: { retries: { type: 'integer', minimum: 0 } },
      },
      audit: { type: 'object', properties: { retainDays: { type: 'integer' } } },
    },
  },
  value: {
    owner: 'finance-ops',
    thresholds: [
      { when: { company: 'Acme', account: '4000' }, value: 0.8, note: 'agreed in March' },
      { when: { company: 'Globex', account: '5000' }, value: 0.65, note: '' },
    ],
    routing: { retries: 2 },
    audit: { retainDays: 90 },
  },
  fields: {
    thresholds: {
      caption: 'Thresholds',
      fields: {
        'when.company': { caption: 'Company', readOnly: true },
        value: { caption: 'Threshold', help: 'Between 0 and 1.' },
      },
    },
    advanced: {
      section: 'Advanced',
      collapsed: true,
      fields: {
        'routing.retries': 'Retries',
        'audit.retainDays': 'Keep audit records for (days)',
      },
    },
  },
})

const rulesValue = pre({ class: 'rules-value' })
const showRules = () => {
  rulesValue.textContent = JSON.stringify(rulesForm.value, null, 2)
}
rulesForm.addEventListener('change', showRules)
showRules()

preview.append(div({ class: 'rules-demo' }, rulesForm, rulesValue))
```
```css
.preview .rules-demo {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
}
.preview .rules-demo tosi-schema-form {
  flex: 1 1 280px;
}
.preview .rules-value {
  flex: 0 1 260px;
  margin: 0;
  font-size: 12px;
}
```
```test
const rulesForm = await waitFor('tosi-schema-form')
await waitMs(100)

test('each array item shows the curated fields, and a section groups the rest', () => {
  const shown = [...rulesForm.querySelectorAll('[data-field]')].map((el) => el.dataset.field)
  expect(shown).toEqual([
    'thresholds.0.when.company',
    'thresholds.0.value',
    'thresholds.1.when.company',
    'thresholds.1.value',
    'routing.retries',
    'audit.retainDays',
  ])
  expect(rulesForm.querySelector('[data-path="thresholds.0.when.company"]').disabled).toBe(true)
  const section = rulesForm.querySelector('details[data-section="advanced"]')
  expect(section.open).toBe(false)
  expect(section.querySelector('summary').textContent).toBe('Advanced')
  expect(section.querySelector('[data-field="routing.retries"]')).not.toBe(null)

  const threshold = rulesForm.querySelector('[data-path="thresholds.1.value"]')
  threshold.value = '0.7'
  threshold.dispatchEvent(new Event('input', { bubbles: true }))
  expect(rulesForm.value.thresholds[1]).toEqual({
    when: { company: 'Globex', account: '5000' },
    value: 0.7,
    note: '',
  })
  threshold.value = '0.65'
  threshold.dispatchEvent(new Event('input', { bubbles: true }))
})
```

## Proposals

A review screen shows what someone, or something, wants to change and lets a person decide.
Set **`proposals`** to a map from path to `{ value, reason?, source? }`, and each one appears
under its field with **Accept** and **Reject**:

```typescript
form.proposals = {
  'thresholds.default': {
    value: 0.9,
    reason: 'Three false escalations last week.',
    source: 'triage agent',
  },
}
```

- `form.value` stays the **current** value. Accepting writes the proposed value and fires
  `proposalaccept`, then `change`; rejecting fires `proposalreject` and leaves the value
  alone. Both carry `detail: { path, proposal }`, and both remove the entry from
  `form.proposals`.
- `acceptProposal(path)` and `rejectProposal(path)` do the same from code.
- Paths are value paths, so an array item is `thresholds.0.value`. A proposal can be for a
  whole array, object or union: it sits at the top of that panel, and accepting it replaces
  the lot. They work with or without a curation; a proposal for something that is not shown
  is named above the fields.
- An object item of an array can be proposed whole (`thresholds.0`).
- Accepting a proposal drops any others for things inside it, since they no longer refer to
  what is there. A proposal for an array item follows its item when items are moved, and is
  dropped when its item is removed. Neither fires an event: read `form.proposals` after a
  `change` if you keep your own list. One for a field inside a union stays at its path if the branch
  changes.
- `reason` and `source` are shown as text, and only when they are strings.
- A field that is `readOnly` in a curation can still have its proposal accepted: that is the
  review screen, where nobody types and somebody decides. A form with `readOnly` set shows
  the proposals with no buttons.
- Set **`proposalView`** to a function to render a proposal yourself. It receives
  `{ path, label, current, proposal, readOnly, accept, reject, elements }` and returns an
  element. While the form is read-only `accept` and `reject` do nothing, and `readOnly` is
  true so your view can leave its buttons out. `acceptProposal()` called from code is not
  affected by `readOnly`.
- Proposals are presentation, like the rest of this page: nothing stops code from writing
  `form.value`. Who may accept is for your application to enforce.

```js
import { tosiSchemaForm } from 'tosijs-ui'
import { elements } from 'tosijs'

const { div, pre } = elements

const reviewForm = tosiSchemaForm({
  schema: {
    type: 'object',
    properties: {
      thresholds: {
        type: 'object',
        properties: {
          default: { type: 'number', minimum: 0, maximum: 1 },
          escalate: { type: 'number', minimum: 0, maximum: 1 },
        },
      },
      routing: {
        type: 'object',
        properties: {
          mode: { type: 'string', enum: ['manual', 'assisted', 'automatic'] },
        },
      },
    },
  },
  value: {
    thresholds: { default: 0.8, escalate: 0.95 },
    routing: { mode: 'assisted' },
  },
  fields: {
    'thresholds.default': { caption: 'Default threshold', readOnly: true },
    'thresholds.escalate': { caption: 'Escalation threshold', readOnly: true },
    'routing.mode': { caption: 'How invoices are routed', readOnly: true },
  },
  proposals: {
    'thresholds.default': {
      value: 0.9,
      reason: 'Three false escalations last week.',
      source: 'triage agent',
    },
    'routing.mode': {
      value: 'automatic',
      reason: 'Assisted routing has matched a person for 30 days.',
      source: 'M. Okafor',
    },
  },
})

const reviewLog = pre({ class: 'review-log' }, 'Nothing decided yet.')
const decided = (verb) => (event) => {
  const { path, proposal } = event.detail
  reviewLog.textContent = `${verb} ${path} = ${JSON.stringify(proposal.value)}`
}
reviewForm.addEventListener('proposalaccept', decided('Accepted'))
reviewForm.addEventListener('proposalreject', decided('Rejected'))

preview.append(reviewForm, reviewLog)
```
```test
const reviewForm = await waitFor('tosi-schema-form')
await waitMs(100)

test('a proposal is shown under its field, and accepting it writes the value', async () => {
  const field = reviewForm.querySelector('[data-field="thresholds.default"]')
  for (let i = 0; i < 50 && !field.querySelector('.schema-proposal'); i += 1) await waitMs(100)
  expect(field.querySelector('.schema-proposal-value').textContent).toBe('Proposed: 0.9')
  expect(field.querySelector('.schema-proposal-source').textContent).toBe('triage agent')
  expect(reviewForm.value.thresholds.default).toBe(0.8)

  const events = []
  const record = (event) => events.push(event.type)
  for (const type of ['proposalaccept', 'proposalreject', 'change']) {
    reviewForm.addEventListener(type, record)
  }
  const proposals = reviewForm.proposals
  expect(reviewForm.acceptProposal('thresholds.default')).toBe(true)
  expect(reviewForm.value.thresholds.default).toBe(0.9)
  expect(events).toEqual(['proposalaccept', 'change'])
  expect(Object.keys(reviewForm.proposals)).toEqual(['routing.mode'])
  expect(reviewForm.acceptProposal('thresholds.default')).toBe(false)

  // put it back, so the example still has something to decide
  reviewForm.value = { ...reviewForm.value, thresholds: { default: 0.8, escalate: 0.95 } }
  reviewForm.proposals = proposals
  for (const type of ['proposalaccept', 'proposalreject', 'change']) {
    reviewForm.removeEventListener(type, record)
  }
})
```

## Read-only

`readOnly` disables the inputs and **hides** the add, remove and reorder controls rather than
grey them out. A dead row of buttons is a form advertising affordances it will not honour;
there is nothing to explain if they are not there.

## Styling

Four custom properties, each with a fallback so the component works unstyled:

| variable | default | what it colours |
| --- | --- | --- |
| `--tosi-error` | `#c00` | the invalid-field outline and the error text |
| `--tosi-border` | `#0002` | the border around a `<details>` section |
| `--tosi-border-radius` | `4px` | that section's corners |
| `--tosi-proposal-bg` | `#fc03` | the background of a proposal |

Spacing comes from the theme's `--tosi-spacing` / `--tosi-spacing-sm`, so a form matches
whatever scale the rest of your page is on.

## Localization

The form's own chrome — the *Add …* button and the reorder tooltips — goes through
[`localize`](/localize/), and the form **rebuilds when the locale changes**, so switching
language does not leave one form behind in the old one.

A curated form adds one more string, `Problems in fields not shown here`, and proposals add
`Proposed`, `Accept`, `Reject` and `Proposed changes to fields not shown here`.

**Labels from your schema are shown as written.** A property's `title`, a curation's
`caption` and `help`, and a section's title are never humanised or passed through
`localize`: they are your data, and a schema generated from data (one property per company,
titled with the company's name) shows those names untouched. Only a property with no `title`
gets a label derived from its key (`firstName` becomes `first name`).

The add button's key is the whole sentence, `Add {item}`, with the item name interpolated
afterwards. Building it as `'Add ' + label` would leave a translator with a dangling fragment
and no way to move the placeholder to where their language puts it.

Labels that come from your **schema** are not localized — a `title` is your string, and a
humanised property name is a data name rather than a UI string. Localize them in the schema if
you need to.

## What it renders today

Scalars and enums: `string` (with `format` picking the input type), `number`, `integer`,
`boolean`, `enum`, `const`, **nested objects**, **arrays** and **unions**. **Tuple arrays
(`prefixItems`) are not supported yet** — a property using one is shown as a placeholder
saying so, rather than being silently omitted. A field that vanishes is indistinguishable from
a schema that never mentioned it, which is how an editor loses data without anyone noticing.

## Properties, methods, events

- `schema` — the JSON Schema. Setting it rebuilds the fields.
- `value` — the data. Setting it updates the inputs; it does **not** fire `change`.
- `readOnly` — disables the inputs.
- `fields` — a curation: which properties to show, in order, with captions and editors.
- `errors` — `{ path, message }[]` for the current value.
- `validate()` — `true` when the value conforms.
- `change` — fires when the user edits a field.

```js
import { tosiSchemaForm } from 'tosijs-ui'

preview.append(
  tosiSchemaForm({
    schema: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        email: { type: 'string', format: 'email' },
        age: { type: 'integer' },
        active: { type: 'boolean' },
      },
      required: ['name'],
    },
    value: { name: 'Ada' },
  })
)
```
```test
const schemaForm = await waitFor('tosi-schema-form')

test('renders one control per schema property', () => {
  expect(schemaForm.querySelectorAll('[data-path]').length).toBeGreaterThan(3)
})

test('editing keeps unknown keys, fires change, and does not rebuild under the user', async () => {
  // ONE test, because these steps share the form. Splitting them made the third read the
  // second's value — the concurrency hazard the live-example docs warn about, hit while
  // writing the component that documents it. (Line comments, not a block: a `*` `/` inside
  // a doc comment closes it early — see #70.)
  schemaForm.value = { name: 'Ada', _id: 'keep-me' }
  await new Promise((r) => requestAnimationFrame(r))
  let fired = 0
  schemaForm.addEventListener('change', () => fired++)

  const input = schemaForm.querySelector('[data-path="name"]')
  input.value = 'Grace'
  input.dispatchEvent(new Event('input', { bubbles: true }))

  // The model owns the data — and a key the schema never mentioned survives the edit,
  // which is the whole reason output is not rebuilt from the inputs.
  expect(schemaForm.value.name).toBe('Grace')
  expect(schemaForm.value._id).toBe('keep-me')
  expect(fired).toBe(1)

  // Now type into it while focused. A render must NOT replace the element or clobber what
  // is being typed — the failure mode of every "the DOM is the model" schema form.
  input.focus()
  input.value = 'Ada Lovelace'
  input.dispatchEvent(new Event('input', { bubbles: true }))
  await new Promise((r) => requestAnimationFrame(r))
  expect(schemaForm.querySelector('[data-path="name"]')).toBe(input)
  expect(input.value).toBe('Ada Lovelace')
  expect(schemaForm.value.name).toBe('Ada Lovelace')
})

```
*/

/*{ "parent": "Components", "description": "<tosi-schema-form> builds an editable, validated form from a JSON Schema, keeping its value, not the inputs, as the source of truth." }*/

import { ElementCreator, elements, withAttributes } from 'tosijs'
import type { JSONSchema } from './schema-form/json-schema.js'
import {
  fieldsFor,
  itemFields,
  branchFields,
  matchBranch,
  selectBranch,
  insertAt,
  removeAt,
  moveItem,
  blankFor,
  getByPath,
  setByPath,
  collectErrors,
  coerceToSchema,
  errorFor,
  relaxInferred,
  type Field,
  type Node,
  type FieldArray,
  type FieldUnion,
  type FieldError,
} from './schema-form/fields.js'
import { localize, i18n } from './localize.js'
import { unenforcedNote } from './schema-form/unenforced.js'
import {
  getSchemaValidator,
  onSchemaValidatorChanged,
  schemaValidationAvailable,
  warnNoValidator,
  warnCannotInfer,
} from './schema-form/validator.js'
export {
  setSchemaValidator,
  schemaValidationAvailable,
  type SchemaValidator,
} from './schema-form/validator.js'
import {
  fieldPlugin,
  onFieldPluginsChanged,
  schemaUsesFormat,
  type FieldPlugin,
} from './schema-form/plugins.js'
import {
  curateNodes,
  editorPlugin,
  findNode,
  hiddenErrors,
  CURATED_KEYS,
  SECTION_KEYS,
  type Curated,
  type CuratedField,
  type FieldCuration,
} from './schema-form/curate.js'
export type {
  FieldCuration,
  CuratedField,
  CuratedSection,
  FieldEditor,
} from './schema-form/curate.js'
export { isSection } from './schema-form/curate.js'

/** A value someone proposes for one field, shown beside the current one for review. */
export interface FieldProposal {
  value: unknown
  /** Why. Shown under the proposed value. */
  reason?: string
  /** Who or what proposed it. */
  source?: string
}

/** What a custom `proposalView` is given. `accept` and `reject` do what the buttons do. */
export interface ProposalContext {
  path: string
  label: string
  current: unknown
  proposal: FieldProposal
  /** The form is read-only: show the proposal, offer no way to act on it. */
  readOnly: boolean
  /** Do nothing while the form is read-only. */
  accept: () => void
  reject: () => void
  elements: typeof elements
}
export {
  registerFieldPlugin,
  type FieldPlugin,
  type FieldPluginContext,
} from './schema-form/plugins.js'

const {
  div,
  label,
  input,
  select,
  option,
  span,
  details,
  summary,
  button,
  ul,
  li,
  b,
  form: formElement,
} = elements

export class TosiSchemaForm extends withAttributes({
  readOnly: false,
}) {
  static preferredTagName = 'tosi-schema-form'

  static lightStyleSpec = {
    ':host': { display: 'block' },
    ':host .schema-form': { display: 'grid', gap: 'var(--tosi-spacing, 10px)' },
    ':host .schema-field': { display: 'grid', gap: '2px' },
    ':host .schema-field > label': { fontSize: '0.9em', opacity: '0.8' },
    ':host .schema-field.-invalid > input, :host .schema-field.-invalid > select':
      { outline: '2px solid var(--tosi-error, #c00)', outlineOffset: '-2px' },
    ':host .schema-error': {
      fontSize: '0.85em',
      color: 'var(--tosi-error, #c00)',
    },
    ':host .schema-error[hidden]': { display: 'none' },
    ':host .schema-group': {
      border: '1px solid var(--tosi-border, #0002)',
      borderRadius: 'var(--tosi-border-radius, 4px)',
      padding: 'var(--tosi-spacing-sm, 8px)',
    },
    ':host .schema-group[open]': {
      display: 'grid',
      gap: 'var(--tosi-spacing, 10px)',
    },
    ':host .schema-group > summary': { cursor: 'pointer', opacity: '0.8' },
    ':host .schema-item': {
      display: 'grid',
      gap: 'var(--tosi-spacing-sm, 8px)',
      gridTemplateColumns: '1fr auto',
      alignItems: 'end',
      borderTop: '1px solid var(--tosi-border, #0001)',
      paddingTop: 'var(--tosi-spacing-sm, 8px)',
    },
    // Every field of an item stacks in the first column; the controls sit beside the first.
    ':host .schema-item > *': { gridColumn: '1' },
    ':host .schema-item > .schema-item-controls': {
      display: 'flex',
      gap: '2px',
      gridColumn: '2',
      gridRow: '1',
    },
    ':host .schema-item-controls[hidden], :host .schema-add[hidden]': {
      display: 'none',
    },
    ':host .schema-union': { display: 'grid', gap: '2px' },
    ':host .schema-variant[hidden]': { display: 'none' },
    ':host .schema-variant-fields': {
      display: 'grid',
      gap: 'var(--tosi-spacing, 10px)',
      paddingTop: 'var(--tosi-spacing-sm, 8px)',
    },
    ':host .schema-unvalidated': {
      fontSize: '0.85em',
      opacity: '0.7',
      fontStyle: 'italic',
    },
    ':host .schema-help': { fontSize: '0.85em', opacity: '0.7' },
    ':host .schema-hidden-errors': {
      fontSize: '0.9em',
      color: 'var(--tosi-error, #c00)',
    },
    ':host .schema-hidden-errors[hidden]': { display: 'none' },
    ':host .schema-hidden-errors ul': {
      margin: '2px 0 0',
      paddingLeft: '1.2em',
    },
    ':host .schema-group.-invalid > summary': {
      color: 'var(--tosi-error, #c00)',
      opacity: '1',
    },
    ':host .schema-proposal': {
      display: 'flex',
      flexWrap: 'wrap' as const,
      alignItems: 'baseline',
      gap: '2px var(--tosi-spacing-sm, 8px)',
      fontSize: '0.9em',
      padding: 'var(--tosi-spacing-sm, 8px)',
      borderRadius: 'var(--tosi-border-radius, 4px)',
      background: 'var(--tosi-proposal-bg, #fc03)',
    },
    ':host .schema-proposal-reason': { flex: '1 1 100%', opacity: '0.8' },
    ':host .schema-proposal-source': { opacity: '0.7', fontStyle: 'italic' },
    ':host .schema-proposal-actions': {
      display: 'flex',
      gap: '4px',
      marginLeft: 'auto',
    },
    ':host .schema-proposal-actions[hidden], :host .schema-hidden-proposals[hidden]':
      { display: 'none' },
    ':host .schema-hidden-proposals': { fontSize: '0.9em', opacity: '0.8' },
    ':host .schema-add': { justifySelf: 'start' },
    ':host .schema-empty': {
      fontSize: '0.9em',
      opacity: '0.7',
      fontStyle: 'italic',
    },
    ':host .schema-unsupported': {
      fontSize: '0.85em',
      opacity: '0.7',
      fontStyle: 'italic',
    },
  }

  private _schema: JSONSchema = {} as JSONSchema
  private _value: any = {}
  private _nodes: Node[] = []
  private _fields: Field[] = []
  /** The schema the current DOM was built for — see `render`. */
  private _builtFor: JSONSchema | null = null
  private _errors: FieldError[] = []
  /*
  path → the elements that path owns, populated as the DOM is built.

  `syncValues` and `syncErrors` each ran one root-scoped attribute `querySelector` PER FIELD,
  on every keystroke — an O(fields) scan repeated O(fields) times. Measured on the built
  component: 200 fields 9.8ms per keystroke, 400 → 40ms, 800 → 240ms; four doublings, each
  quadrupling. At 3200 fields validation itself was 0.19ms against 315ms of lookup — a ratio
  of 1600:1, so the cost was never the validating.

  A Map turns both passes linear. It is rebuilt whenever the DOM is, which is the only time
  the answers can change.
  */
  private _labelCount = 0

  /*
  A field's label, tied to its control (#3574): a <label> beside an input names nothing until it
  says which input it is for. A plugin's control may not be labelable (a <div> wrapping its own
  inputs), so that one is named by the label's id instead.
  */
  private labelFor(text: string, control: HTMLElement): HTMLLabelElement {
    const id = `${this.instanceId}-field-${++this._labelCount}`
    const element = label(text)
    const labelable = (el: Element) =>
      /^(INPUT|SELECT|TEXTAREA|BUTTON|METER|OUTPUT|PROGRESS)$/.test(
        el.tagName
      ) || !!(el.constructor as { formAssociated?: boolean }).formAssociated
    // A plugin's wrapper is labelled through the first control inside it.
    const target = labelable(control)
      ? control
      : (control.querySelector('input, select, textarea') as HTMLElement | null)
    if (target) {
      target.id ||= id
      element.htmlFor = target.id
    } else {
      element.id = `${id}-label`
      control.setAttribute('aria-labelledby', element.id)
    }
    return element
  }

  private _index = new Map<
    string,
    { control: HTMLElement; wrapper: HTMLElement; errorSlot: HTMLElement }
  >()

  /*
  Curation (#221): which properties to show, in what order, under what captions, and through
  which editors. Applied to the node list, so everything downstream (building, syncing,
  errors) works on the curated nodes exactly as it does on the full ones.
  */
  private _curation: FieldCuration | null = null
  /*
  The curation entry for each path that is shown, by ABSOLUTE path. The root's are known when
  the form is built; an array item's and a union branch's are added as those are expanded,
  since which paths exist there depends on the value.
  */
  private _entries = new Map<string, CuratedField>()
  /*
  Where a proposal goes for a path that is a PANEL (an object, an array, a union) and so has
  no entry in `_index`: the panel, and the element the proposal is placed after.
  */
  private _hosts = new Map<
    string,
    { wrapper: HTMLElement; after: Element | null }
  >()
  private _arrays = new Map<string, FieldArray>()

  /** The array at `path`, or none: a value that is not an array there has no items. */
  private listAt(path: string): unknown[] {
    const list = getByPath(this._value, path)
    return Array.isArray(list) ? list : []
  }

  /** Everything under `path` is about to be recreated: what is held for it is stale. */
  private forgetUnder(path: string): void {
    const prefix = `${path}.`
    for (const map of [this._index, this._hosts, this._arrays]) {
      for (const key of [...map.keys()]) {
        if (key.startsWith(prefix)) map.delete(key)
      }
    }
  }
  private _curationWarnings = new Set<string>()
  private _hiddenErrorSlot: HTMLElement | null = null
  private _hiddenErrorSignature = ''

  /**
   * Show only these properties. Keys are paths into the schema (`thresholds.default`), in
   * display order; each value is `{ caption?, help?, readOnly?, editor?, fields? }`, a
   * caption string, `true`, or a `{ section, fields }` panel. `null` shows everything the
   * schema describes.
   *
   * The form still holds and validates the WHOLE value: a property that is not shown keeps
   * its value, and an error in one is listed above the fields.
   */
  get fields(): FieldCuration | null {
    return this._curation
  }

  set fields(fields: FieldCuration | null) {
    this._curation = fields ?? null
    this.rebuild()
  }

  /** A curated `editor` first, then the `format` plugins. A section is neither. */
  private pluginFor(node: Node): FieldPlugin | undefined {
    if ((node as { section?: string }).section !== undefined) return undefined
    const editor = this._entries.get(node.path)?.editor
    return editor ? editorPlugin(editor) : fieldPlugin(node.schema?.format)
  }

  /*
  Take a curation's entries into the form and say what was wrong with it, once per build.
  Array indices are written `[]` in a warning, or every item would repeat it.
  */
  private adopt(result: Curated): Node[] {
    for (const [path, entry] of result.entries) this._entries.set(path, entry)
    const generic = (path: string) => path.replace(/\.\d+(?=\.|$)/g, '[]')
    const warn = (problem: string) => {
      if (this._curationWarnings.has(problem)) return
      this._curationWarnings.add(problem)
      console.warn(`<tosi-schema-form>: \`fields\` ${problem}`)
    }
    if (result.unknown.length > 0) {
      warn(
        `names path(s) the schema does not describe: ${[
          ...new Set(result.unknown.map(generic)),
        ].join(', ')}`
      )
    }
    if (result.overlapping.length > 0) {
      warn(
        `names a property and something inside it; the first is shown, skipped: ${[
          ...new Set(result.overlapping.map(generic)),
        ].join(', ')}`
      )
    }
    for (const { path, keys } of result.badKeys) {
      warn(
        `['${generic(path)}'] has unknown key(s) ${keys.join(
          ', '
        )}; a field's keys are ${CURATED_KEYS.join(
          ', '
        )}, a section's ${SECTION_KEYS.join(', ')}`
      )
    }
    return result.nodes
  }

  /** One array element's nodes, curated when the array's entry has `fields`. */
  private itemNodes(node: FieldArray, index: number): Node[] {
    const nodes = itemFields(node.itemSchema, node.path, index)
    const inner = this._entries.get(node.path)?.fields
    if (!inner) return nodes
    return this.adopt(curateNodes(nodes, inner, `${node.path}.${index}`))
  }

  /*
  The active branch's nodes, curated when the union's entry has `fields`. A path that some
  OTHER branch describes is left out quietly: one curation covers every branch, and each
  shows the fields it has.
  */
  private branchNodes(node: FieldUnion): Node[] {
    const nodes = branchFields(node, this._value)
    const inner = this._entries.get(node.path)?.fields
    if (!inner) return nodes
    const elsewhere = (path: string) =>
      node.branches.some(
        (branch) => findNode(fieldsFor(branch.schema, node.path), path) != null
      )
    return this.adopt(curateNodes(nodes, inner, node.path, elsewhere))
  }

  /**
   * Values proposed for review, by path: `{ value, reason?, source? }`. Each is shown under
   * its field with Accept and Reject. `value` stays the CURRENT value until one is
   * accepted. `null` (the default) shows none.
   */
  get proposals(): Record<string, FieldProposal> | null {
    return this._proposals
  }

  set proposals(proposals: Record<string, FieldProposal> | null) {
    this._proposals = proposals ?? null
    this.queueRender()
  }

  private _proposals: Record<string, FieldProposal> | null = null

  /** Render a proposal your own way. Return the element to show under the field. */
  proposalView: ((context: ProposalContext) => HTMLElement) | null = null

  private settleProposal(path: string, type: string): FieldProposal | null {
    const proposal = this._proposals?.[path]
    if (!proposal) return null
    const rest = { ...this._proposals }
    delete rest[path]
    this._proposals = rest
    this.dispatchEvent(
      new CustomEvent(type, { bubbles: true, detail: { path, proposal } })
    )
    return proposal
  }

  /*
  Deciding a proposal removes the buttons the reviewer was on. Focus goes to the field if
  it can be edited, else to the next proposal still to decide, so a keyboard user is not
  dropped at the top of the page.
  */
  private focusAfterProposal(path: string, hadFocus: boolean): void {
    if (!hadFocus) return
    /*
    Try each candidate until one takes focus. Whether an element CAN is not readable up
    front: a button inside a closed `<details>` still reports a box in Chromium, and
    `focus()` on it silently does nothing.
    */
    const control = this._index.get(path)?.control
    const candidates = [
      control?.matches('input, select, textarea') ? control : null,
      ...this.querySelectorAll('.schema-accept'),
      ...this.querySelectorAll('.schema-proposal button'),
    ]
    for (const el of candidates) {
      if (!(el instanceof HTMLElement) || (el as HTMLInputElement).disabled) {
        continue
      }
      el.focus()
      if (document.activeElement === el) return
    }
  }

  private proposalHasFocus(path: string): boolean {
    return [...this.querySelectorAll('.schema-proposal')].some(
      (el) =>
        (el as HTMLElement).dataset.proposal === path &&
        el.contains(document.activeElement)
    )
  }

  /**
   * Write the proposed value at `path` and drop the proposal. Fires `proposalaccept`
   * (`detail: { path, proposal }`), then `change`. Returns false when there is none.
   */
  acceptProposal(path: string): boolean {
    const proposal = this._proposals?.[path]
    if (!proposal) return false
    const hadFocus = this.proposalHasFocus(path)
    this._value = setByPath(this._value, path, proposal.value)
    this.settleProposal(path, 'proposalaccept')
    /*
    Whatever was proposed for something INSIDE what was just replaced no longer refers to
    what is there: a proposal for `list.0.value` would attach to the new first item.
    */
    const inside = Object.keys(this._proposals ?? {}).filter((key) =>
      key.startsWith(`${path}.`)
    )
    if (inside.length > 0) {
      const rest = { ...this._proposals }
      for (const key of inside) delete rest[key]
      this._proposals = rest
    }
    // The value may be a whole array, object or union: bring the structure up to date.
    this.syncArrays()
    this.syncVariants()
    this.afterStructuralEdit()
    this.focusAfterProposal(path, hadFocus)
    return true
  }

  /** Drop the proposal at `path`, leaving the value alone. Fires `proposalreject`. */
  rejectProposal(path: string): boolean {
    const hadFocus = this.proposalHasFocus(path)
    if (!this.settleProposal(path, 'proposalreject')) return false
    this.syncProposals()
    this.focusAfterProposal(path, hadFocus)
    return true
  }

  /*
  Proposals are keyed by value path, and an array item's path holds its index. When items
  move, the proposals move with them; one for a removed item is dropped, since the thing it
  proposed to change is gone.
  */
  private rekeyProposals(
    arrayPath: string,
    newIndex: (index: number) => number | null
  ): void {
    if (!this._proposals) return
    const prefix = `${arrayPath}.`
    let changed = false
    const next: Record<string, FieldProposal> = {}
    for (const [path, proposal] of Object.entries(this._proposals)) {
      const match = path.startsWith(prefix)
        ? /^(\d+)(.*)$/.exec(path.slice(prefix.length))
        : null
      if (!match) {
        next[path] = proposal
        continue
      }
      const index = newIndex(Number(match[1]))
      if (index !== Number(match[1])) changed = true
      if (index !== null) next[`${prefix}${index}${match[2]}`] = proposal
    }
    if (changed) this._proposals = next
  }

  get schema(): JSONSchema {
    return this._schema
  }

  set schema(schema: JSONSchema) {
    this._schema = schema ?? ({} as JSONSchema)
    this.queueRender()
  }

  get value(): any {
    return this._value
  }

  /** Setting `value` updates the inputs. It does NOT fire `change` — that is for edits. */
  set value(value: any) {
    this._value = value ?? {}
    this.queueRender()
  }

  /** `{ path, message }[]` for the current value, or `[]` with no validator installed. */
  get errors(): FieldError[] {
    return this._errors
  }

  /** Does the current value conform? `true` when no validator is installed. */
  validate(): boolean {
    this.refreshErrors()
    return this._errors.length === 0
  }

  /** Is anything actually checking this form? See `validate()`. */
  get validationAvailable(): boolean {
    return schemaValidationAvailable()
  }

  private refreshErrors(): void {
    const validator = getSchemaValidator()
    if (!validator) {
      warnNoValidator('<tosi-schema-form>')
      this._errors = []
      return
    }
    // Every path with an error slot, which includes plugin and custom-editor fields: a
    // "Missing x" is re-keyed onto its field only when that field is known.
    const paths = [...this._fields.map((f) => f.path), ...this._index.keys()]
    this._errors = collectErrors(
      (onError) =>
        validator.validate(this._value, this._schema, {
          onError,
          /*
          Not sampling mode. Without `strict`, their validator samples: `maxProperties` is
          skipped entirely and a bad element deep in a long array can be stepped over. The
          form would say valid and the consumer's save path — or the server — would not.
          */
          strict: true,
        }),
      paths
    )
  }

  /*
  Coerce a control's string back to what the schema asked for.

  An empty numeric field becomes `undefined` rather than `0` or `NaN`: "the user cleared it"
  and "the user typed zero" are different states, and conflating them writes a value nobody
  entered.
  */
  /*
  Named `handle…`, not `on…`, and that is a framework contract rather than taste.

  tosijs's element factory treats an `on<Event>` property name as event-handler sugar —
  `creator({ onClick })` attaches a listener rather than assigning the property — so a
  component member called `onFieldInput` is shadowed and cannot be set or read through the
  creator. It warned about exactly this in our own doc site's console.
  */
  private handleFieldInput = (event: Event): void => {
    const el = event.target as HTMLInputElement | HTMLSelectElement
    const path = el.dataset?.path
    if (!path) return
    /*
    The input's own event stops here; the form re-emits its own.

    Native `input` and `change` both bubble out of the light DOM, so a consumer listening on
    the form received the control's events as well as ours — indistinguishable except by
    checking for a property that should always be there. A checkbox click produced THREE:
    two synthetic (this handler wired to both `onInput` and `onChange`) plus the native one.
    `<tosi-table>` fixed exactly this hazard in the same release; the sibling shipped without
    it because its doc test dispatched a bare `input` and never blurred.
    */
    event.stopPropagation()
    const field = this._fields.find((f) => f.path === path)
    if (!field) return
    this._value = setByPath(
      this._value,
      path,
      coerceToSchema(field, el.value, (el as HTMLInputElement).checked, el.type)
    )
    this.refreshErrors()
    this.syncErrors()
    this.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /*
  A nested object renders as an OPEN `<details>`.

  Open by default: a form whose fields are hidden behind closed sections looks empty, and a
  user who does not know a section exists cannot fill it in. Collapsing is something the user
  decides once a part is done — it is not a sensible initial state for an editor.
  */
  private buildNode(node: Node): HTMLElement {
    const el = this.buildBare(node)
    const section = (node as { section?: string }).section
    if (section !== undefined) {
      this._watchPanels = true
      el.dataset.watch = ''
      el.classList.add('schema-section')
      el.dataset.section = section
      if ((node as { collapsed?: boolean }).collapsed) {
        ;(el as HTMLDetailsElement).open = false
      }
      return el
    }
    // What the curation asked for on this node: help, a lock, a closed panel.
    const entry = this._entries.get(node.path)
    if (!entry) return el
    if (entry.help) {
      const help = span({ class: 'schema-help' }, String(entry.help))
      const errorSlot = this._index.get(node.path)?.errorSlot
      if (errorSlot?.parentElement === el) el.insertBefore(help, errorSlot)
      else if (el.firstElementChild?.tagName === 'SUMMARY') {
        el.firstElementChild.after(help)
      } else el.append(help)
    }
    if (entry.readOnly) el.dataset.locked = ''
    if (entry.collapsed && el instanceof HTMLDetailsElement) {
      el.open = false
      this._watchPanels = true
      el.dataset.watch = ''
    }
    return el
  }

  private buildBare(node: Node): HTMLElement {
    /*
    A plugin is tried FIRST, and for every kind of node — a registered `format` claims the
    property whatever shape it is, so a plugin can render something the form has no control
    for at all (its own `unsupported` placeholder is the fallback, not the ceiling).
    */
    const plugin = this.pluginFor(node)
    if (plugin) return this.buildPluginField(node, plugin)
    if (node.kind === 'array') return this.buildArray(node)
    if (node.kind === 'union') return this.buildUnion(node)
    if ('children' in node) {
      const heading = summary(String(node.label), ...this.unvalidatedNote(node))
      const panel = details(
        { class: 'schema-group', open: true },
        heading,
        ...node.children.map((child) => this.buildNode(child))
      )
      if (node.section === undefined) {
        this._hosts.set(node.path, { wrapper: panel, after: heading })
      }
      return panel
    }
    return this.buildField(node)
  }

  /*
  An array renders as its current elements plus an Add button.

  Add / remove / move edit the MODEL and then rebuild only THIS array's container — not the
  whole form. Rebuilding everything would throw away focus, scroll and every open `<details>`
  elsewhere on the page, which is the failure this component exists to avoid; the array's own
  items genuinely did change, so rebuilding those is honest.
  */
  private buildArray(node: FieldArray): HTMLElement {
    const heading = summary(String(node.label), ...this.unvalidatedNote(node))
    const container = details(
      { class: 'schema-group schema-array', open: true },
      heading
    )
    container.dataset.array = node.path
    this._hosts.set(node.path, { wrapper: container, after: heading })
    this._arrays.set(node.path, node)
    this.fillArray(container, node)
    return container
  }

  private fillArray(container: HTMLElement, node: FieldArray): void {
    const list = this.listAt(node.path)
    // Keep the summary; replace the items. Everything under this path is about to be
    // recreated, so its index entries are stale by definition.
    this.forgetUnder(node.path)
    for (const child of [...container.children]) {
      if (child.matches('.schema-item, .schema-add')) child.remove()
    }
    list.forEach((_item, index) => {
      const row = div({ class: 'schema-item' })
      // An object item can be proposed whole; a scalar item is a field and has its own slot.
      if (node.itemSchema?.properties) {
        this._hosts.set(`${node.path}.${index}`, { wrapper: row, after: null })
      }
      const fields = this.itemNodes(node, index)
      row.append(...fields.map((f) => this.buildNode(f)))
      row.append(
        div(
          { class: 'schema-item-controls' },
          button(
            {
              type: 'button',
              class: 'schema-move-up',
              // The tooltip is localized, so it is NOT a selector — the class is.
              title: localize('move up'),
              ariaLabel: localize('move up'),
              disabled: index === 0,
              onClick: () =>
                this.moveArrayItem(node, index, index - 1, 'schema-move-up'),
            },
            '↑'
          ),
          button(
            {
              type: 'button',
              class: 'schema-move-down',
              // The tooltip is localized, so it is NOT a selector — the class is.
              title: localize('move down'),
              ariaLabel: localize('move down'),
              disabled: index === list.length - 1,
              onClick: () =>
                this.moveArrayItem(node, index, index + 1, 'schema-move-down'),
            },
            '↓'
          ),
          button(
            {
              type: 'button',
              class: 'schema-remove',
              // The tooltip is localized, so it is NOT a selector — the class is.
              title: localize('remove'),
              ariaLabel: localize('remove'),
              onClick: () => this.removeArrayItem(node, index),
            },
            '✕'
          )
        )
      )
      container.append(row)
    })
    container.append(
      button(
        {
          type: 'button',
          class: 'schema-add',
          onClick: () => this.addArrayItem(node),
        },
        localize('Add {item}', { item: node.label })
      )
    )
  }

  /*
  A variant union renders as a picker plus the fields of the branch it currently matches.

  The picker is the discriminator — that field is deliberately not rendered a second time
  underneath, because two controls for one value is an invitation to set them differently.
  */
  private buildUnion(node: FieldUnion): HTMLElement {
    const picker = select(
      {
        class: 'schema-variant',
        onChange: (event: Event) => this.handleVariantChange(node, event),
      },
      /*
      A hidden empty option, so a value matching NO branch shows as "—" rather than silently
      claiming to be the first variant. Hidden rather than absent because the picker element
      is reused: adding and removing an option would change the control under the user, and
      "no variant" is not something they can choose — only something the data can be.
      */
      option({ value: '', hidden: true }, '—'),
      ...node.branches.map((branch, i) =>
        option({ value: String(i) }, String(branch.label))
      )
    )
    // The curation may hide the picker: a screen about one variant has no use for it.
    if (this._entries.get(node.path)?.picker === false) picker.hidden = true
    const container = div(
      { class: 'schema-field schema-union' },
      this.labelFor(String(node.label), picker),
      picker,
      ...this.unvalidatedNote(node),
      div({ class: 'schema-variant-fields' })
    )
    container.dataset.union = node.path
    this._hosts.set(node.path, { wrapper: container, after: picker })
    this.fillUnion(container, node)
    return container
  }

  /*
  Only the branch's FIELDS are rebuilt — the picker itself is left alone.

  Replacing it would take focus off the control the user just operated, which is the exact
  failure this component exists to avoid; changing variant with the keyboard would drop you
  back to the top of the form on every keystroke.
  */
  private fillUnion(container: HTMLElement, node: FieldUnion): void {
    const index = matchBranch(node.branches, getByPath(this._value, node.path))
    const picker = container.querySelector(
      '.schema-variant'
    ) as HTMLSelectElement
    const next = index === -1 ? '' : String(index)
    if (picker && picker.value !== next) picker.value = next
    const body = container.querySelector(
      '.schema-variant-fields'
    ) as HTMLElement
    // Everything under this union is about to be recreated: its index entries are stale.
    this.forgetUnder(node.path)
    body.replaceChildren(
      ...this.branchNodes(node).map((child) => this.buildNode(child))
    )
    container.dataset.branch = String(index)
  }

  private handleVariantChange(node: FieldUnion, event: Event): void {
    const index = Number((event.target as HTMLSelectElement).value)
    if (!Number.isInteger(index)) return
    this._value = selectBranch(this._value, node, index)
    const container = this.querySelector(
      `[data-union="${CSS.escape(node.path)}"]`
    ) as HTMLElement | null
    if (container) this.fillUnion(container, node)
    this.afterStructuralEdit()
  }

  /*
  Re-fill any array whose rows no longer match its value's length.

  `form.value = {...}` does not rebuild the form (the schema did not change), and a leaf's
  value is synced in place, but how many rows an array has is a fact about the VALUE. Without
  this a value with more items showed the old rows, and one with fewer left controls for
  items that no longer exist. Outer arrays first: re-filling one recreates what is inside it.
  */
  private syncArrays(): void {
    for (const container of this.querySelectorAll('[data-array]')) {
      if (!container.isConnected) continue
      const path = (container as HTMLElement).dataset.array!
      const node = this._arrays.get(path)
      if (!node) continue
      const list = getByPath(this._value, path)
      const length = Array.isArray(list) ? list.length : 0
      const rows = [...container.children].filter((el) =>
        el.classList.contains('schema-item')
      ).length
      if (rows !== length) this.fillArray(container as HTMLElement, node)
    }
  }

  /*
  Re-fill any union whose value no longer matches the branch on screen.

  This is what makes `form.value = {...}` work for variants: the schema did not change, so
  the form is not rebuilt, but the fields a union shows are a function of the VALUE. Unions
  nested inside a re-filled union are handled by the outer refill and then simply agree.
  */
  private syncVariants(): void {
    for (const node of this.expanded()) {
      if (node.kind !== 'union') continue
      const container = this.querySelector(
        `[data-union="${CSS.escape(node.path)}"]`
      ) as HTMLElement | null
      if (!container) continue
      const index = matchBranch(
        node.branches,
        getByPath(this._value, node.path)
      )
      if (container.dataset.branch !== String(index))
        this.fillUnion(container, node)
    }
  }

  /** The shared tail of every edit that changes WHICH fields exist. */
  private afterStructuralEdit(): void {
    this._fields = this.allFields()
    this.applyReadOnly()
    this.syncValues()
    this.syncPlugins()
    this.refreshErrors()
    this.syncErrors()
    this.syncProposals()
    this.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /*
  Rebuild the array, then put focus back where the user left it.

  `fillArray` destroys and recreates every row — the comments elsewhere claimed only the
  edited one was rebuilt, which was true one level too coarse: clicking ↓ on a five-item array
  left `document.activeElement === document.body` and none of the five row elements survived.
  You could not press ↓ twice to move an item two places, or add two rows from the keyboard,
  because the button you had just clicked no longer existed.

  Restoring focus by ROLE AND INDEX rather than reconciling the DOM is the cheap fix and it is
  the one that matches what the user is doing: they clicked "move down" on row 3, so afterwards
  they want "move down" on row 4. Reconciliation would also preserve scroll and any open
  `<details>`, and it is the right answer eventually — filed rather than rushed, because it
  changes how every array row is built.
  */
  private afterArrayEdit(
    node: FieldArray,
    focusTo?: { role?: string; index: number }
  ): void {
    const container = this.querySelector(
      `[data-array="${CSS.escape(node.path)}"]`
    ) as HTMLElement | null
    if (container) this.fillArray(container, node)
    this.afterStructuralEdit()
    if (!container || !focusTo) return
    const rows = container.querySelectorAll('.schema-item')
    const row = rows[Math.min(focusTo.index, rows.length - 1)]
    /*
    The intended control may not be focusable where the item landed.

    Move an item to the last position and its "move down" is disabled — and a disabled button
    cannot take focus, so aiming at it put focus on `<body>`, which is the bug this restore
    exists to fix. Prefer the control you clicked, fall back to anything focusable in the row
    it moved to, and only then to Add (which is where a delete of the last item belongs).
    */
    const FOCUSABLE = 'INPUT SELECT BUTTON TEXTAREA'
    const usable = (el: Element | null | undefined): HTMLElement | null =>
      el &&
      FOCUSABLE.includes(el.tagName) &&
      !(el as HTMLButtonElement).disabled
        ? (el as HTMLElement)
        : null
    const target =
      usable(focusTo.role ? row?.querySelector(`.${focusTo.role}`) : null) ??
      usable(
        row?.querySelector(
          'input:not([disabled]), select:not([disabled]), button:not([disabled])'
        )
      ) ??
      usable(container.querySelector('.schema-add'))
    target?.focus()
  }

  private addArrayItem(node: FieldArray): void {
    const list = this.listAt(node.path)
    this._value = insertAt(
      this._value,
      node.path,
      list.length,
      blankFor(node.itemSchema)
    )
    // Land in the new row's first control — no role, because "Add" has no button to return
    // to; what you want is to start typing in what you just made.
    this.afterArrayEdit(node, { index: list.length })
  }

  private removeArrayItem(node: FieldArray, index: number): void {
    this._value = removeAt(this._value, node.path, index)
    this.rekeyProposals(node.path, (i) =>
      i === index ? null : i > index ? i - 1 : i
    )
    // Removing row N leaves the cursor on what is now row N — the next item down, which is
    // where a repeated delete should land.
    this.afterArrayEdit(node, { role: 'schema-remove', index })
  }

  private moveArrayItem(
    node: FieldArray,
    from: number,
    to: number,
    role = 'schema-move-down'
  ): void {
    this._value = moveItem(this._value, node.path, from, to)
    this.rekeyProposals(node.path, (i) =>
      i === from
        ? to
        : from < to && i > from && i <= to
        ? i - 1
        : to < from && i >= to && i < from
        ? i + 1
        : i
    )
    // Follow the item: you moved row 3 down, so the button you want next is on row 4.
    this.afterArrayEdit(node, { role, index: to })
  }

  /*
  Everything currently on screen — leaves and unions — with arrays expanded to the elements
  the CURRENT value has and unions to the branch it currently matches.

  Recomputed after any structural edit because the field set genuinely changed, which is
  exactly why `leafFields` refuses to guess at array or union leaves from the schema alone.
  */
  private expanded(): Node[] {
    const expand = (nodes: Node[]): Node[] =>
      nodes.flatMap((node) => {
        // A plugin-claimed node is a LEAF whatever its shape — the plugin renders it whole,
        // so the form must not also expand its children into fields nobody rendered.
        if (this.pluginFor(node)) return [node]
        if (node.kind === 'union')
          return [node, ...expand(this.branchNodes(node))]
        if (node.kind === 'array') {
          const list = this.listAt(node.path)
          return list.flatMap((_x, i) => expand(this.itemNodes(node, i)))
        }
        if ('children' in node) return expand(node.children)
        return [node]
      })
    return expand(this._nodes)
  }

  /** Every leaf that carries a value — the union pickers are structure, not data. */
  private allFields(): Field[] {
    return this.expanded().filter(
      (node) => node.kind !== 'union' && !this.pluginFor(node)
    ) as Field[]
  }

  /** The plugin-rendered fields, which sync through the plugin rather than the DOM. */
  private pluginFields(): Array<{ node: Node; plugin: FieldPlugin }> {
    return this.expanded().flatMap((node) => {
      const plugin = this.pluginFor(node)
      return plugin ? [{ node, plugin }] : []
    })
  }

  /*
  Read-only DISABLES the inputs and HIDES the structural controls.

  Hiding rather than disabling is deliberate: a greyed-out "Add" and a row of dead ↑ ↓ ✕
  buttons is a form telling the user about affordances it will not honour. There is nothing
  to explain if they are not there. (The component this design learned from disabled them —
  its SF-5.) Whole-form and idempotent, so it can run after any partial refill.
  */
  private applyReadOnly(): void {
    // A curated field can be read-only on its own (`[data-locked]`).
    const locked = (el: Element) =>
      this.readOnly || el.closest('[data-locked]') != null
    for (const el of this.querySelectorAll('input, select')) {
      ;(el as HTMLInputElement).disabled = locked(el)
    }
    /*
    A textarea, or a custom editor that is itself a component, is locked through its own
    `disabled` (or, failing that, `readOnly`) property. The form only undoes what it did: one a plugin disabled for its own reasons
    stays disabled when the form is editable.
    */
    for (const el of this.querySelectorAll('textarea, [data-plugin]')) {
      if (el.tagName !== 'TEXTAREA' && !el.tagName.includes('-')) continue
      // Components differ in which they implement; `<tosi-rating>` has only `readonly`.
      const prop = ['disabled', 'readOnly', 'readonly'].find((p) => p in el)
      if (!prop) continue
      const control = el as any
      if (locked(el)) {
        if (!control[prop]) {
          control[prop] = true
          control.dataset.lockedByForm = ''
        }
      } else if ('lockedByForm' in control.dataset) {
        control[prop] = false
        delete control.dataset.lockedByForm
      }
    }
    for (const el of this.querySelectorAll(
      '.schema-item-controls, .schema-add'
    )) {
      ;(el as HTMLElement).hidden = locked(el)
    }
  }

  /*
  The "this keyword is not validated" note, for ANY field that needs it.

  It used to render only for unions, which meant the honesty it exists for applied to exactly
  one keyword. `{score: {type: 'number', exclusiveMinimum: 0}}` with `score: 0` rendered a
  clean field and `validate()` returned true — a green, error-free form over a value the
  schema forbids, which is precisely what `unenforced.ts` was written to prevent and what the
  docs promise it prevents.
  */
  private unvalidatedNote(node: { unvalidated?: string[] }): HTMLElement[] {
    return node.unvalidated?.length
      ? [
          span(
            { class: 'schema-unvalidated' },
            unenforcedNote(node.unvalidated)
          ),
        ]
      : []
  }

  /*
  A plugin owns the CONTROL; the form still owns the label and the error slot.

  That split is deliberate. A plugin that had to render its own label and error would have to
  re-implement them to match, and every plugin would drift a little — and the error is the
  form's knowledge, not the plugin's.
  */
  private buildPluginField(node: Node, plugin: FieldPlugin): HTMLElement {
    const control = plugin.render({
      schema: node.schema,
      path: node.path,
      label: node.label,
      required: node.required,
      value: getByPath(this._value, node.path),
      set: (value: unknown) => {
        this._value = setByPath(this._value, node.path, value)
        this.refreshErrors()
        this.syncErrors()
        this.dispatchEvent(new Event('change', { bubbles: true }))
      },
      elements,
    })
    control.dataset.plugin = node.path
    const errorSlot = span({ class: 'schema-error', hidden: true })
    const wrapper = div(
      { class: 'schema-field' },
      this.labelFor(String(node.label), control),
      control,
      ...this.unvalidatedNote(node as { unvalidated?: string[] }),
      errorSlot
    )
    wrapper.dataset.field = node.path
    this._index.set(node.path, { control, wrapper, errorSlot })
    return wrapper
  }

  private buildField(field: Field): HTMLElement {
    if (field.kind === 'unsupported') {
      return div(
        { class: 'schema-field' },
        label(String(field.label)),
        span({ class: 'schema-unsupported' }, `${field.reason}`)
      )
    }
    // `dataset` is set imperatively, not through the element creator: passing
    // `{ dataset: {...} }` makes tosijs assign to `element.dataset`, which is readonly, and
    // the resulting "Attempted to assign to readonly property" aborts the whole render with
    // no clue which prop caused it.
    const shared: any = { onInput: this.handleFieldInput }
    const control =
      field.kind === 'enum'
        ? select(
            shared,
            ...(field.required ? [] : [option({ value: '' }, '—')]),
            ...(field.options ?? []).map((o) =>
              option({ value: String(o.value) }, String(o.label))
            )
          )
        : field.kind === 'boolean'
        ? // `onInput` from `shared` already covers a checkbox — a click fires both, so wiring
          // `onChange` too dispatched the model write twice.
          input({ ...shared, type: 'checkbox' })
        : input({
            ...shared,
            type:
              field.kind === 'number' || field.kind === 'integer'
                ? 'number'
                : field.inputType ?? 'text',
            ...(field.kind === 'integer' ? { step: 1 } : {}),
            ...(field.required ? { required: true } : {}),
            ...(field.kind === 'const' ? { readonly: true } : {}),
          })
    /*
    The control's native `change` stops at the control.

    `handleFieldInput` is wired to `onInput` only, so `stopPropagation()` inside it never sees
    a `change` event — and native `change` bubbles out of the light DOM, so a consumer
    listening on the form still received the input's own event alongside ours. `<tosi-table>`
    fixed the same hazard by stopping it in its `change` handler; the form needs an explicit
    listener because it does not have one.
    */
    control.addEventListener('change', (event) => event.stopPropagation())
    control.dataset.path = field.path
    const errorSlot = span({ class: 'schema-error', hidden: true })
    const wrapper = div(
      { class: 'schema-field' },
      this.labelFor(String(field.label), control),
      control,
      ...this.unvalidatedNote(field),
      errorSlot
    )
    wrapper.dataset.field = field.path
    this._index.set(field.path, { control, wrapper, errorSlot })
    return wrapper
  }

  /*
  Put the current value into the controls — WITHOUT touching the one being typed in.

  Skipping the focused element is the whole point. A form that writes every value on every
  render moves the caret to the end mid-word, and one that rebuilds the tree destroys focus
  outright. Both are what "the DOM is the model" designs do by construction, and both are the
  class of bug this project has spent real time removing from `<tosi-table>`.
  */
  private syncValues(): void {
    for (const field of this._fields) {
      const el = this._index.get(field.path)?.control as
        | HTMLInputElement
        | HTMLSelectElement
        | undefined
      if (!el || el === document.activeElement) continue
      const current = getByPath(this._value, field.path)
      if (field.kind === 'boolean') {
        ;(el as HTMLInputElement).checked = current === true
        continue
      }
      const next =
        current === undefined || current === null ? '' : String(current)
      if (el.value !== next) el.value = next
    }
  }

  /*
  Plugin controls take their value through the plugin's own `sync`, never by having their
  element replaced — the form has no idea what is inside one, and a slider being dragged is
  exactly the thing a rebuild would ruin.
  */
  private syncPlugins(): void {
    for (const { node, plugin } of this.pluginFields()) {
      const el = this.querySelector(
        `[data-plugin="${CSS.escape(node.path)}"]`
      ) as HTMLElement | null
      if (!el || el.contains(document.activeElement)) continue
      plugin.sync(el, getByPath(this._value, node.path))
    }
  }

  private syncErrors(): void {
    /*
    Walks the index, not `_fields`: the index holds every path with an error slot, which
    includes plugin and custom-editor fields. `_fields` leaves those out, so an error on one
    was never shown. The slot is held, not re-found, so this stays one pass with no DOM
    queries on the keystroke path.
    */
    for (const [path, entry] of this._index) {
      const message = errorFor(this._errors, path)
      entry.wrapper.classList.toggle('-invalid', Boolean(message))
      entry.errorSlot.textContent = message ?? ''
      entry.errorSlot.hidden = !message
    }
    if (this._watchPanels) {
      /*
      A panel that starts closed must not hide that something inside it is wrong. This is a
      DOM query on the keystroke path, unlike the loop above, and runs only for a form that
      has such panels: one query per panel, and there are few of them.
      */
      for (const panel of this.querySelectorAll('[data-watch]')) {
        panel.classList.toggle(
          '-invalid',
          panel.querySelector('.schema-field.-invalid') != null
        )
      }
    }
    this.syncHiddenErrors()
  }

  private _watchPanels = false
  private _hiddenProposalSlot: HTMLElement | null = null

  /*
  Put each proposal under its field, or at the top of its panel. Idempotent, and keyed on
  what the proposal says, so a render that changes nothing leaves the element (and focus
  inside it) alone.
  */
  private syncProposals(): void {
    const proposals = this._proposals
    if (!proposals && !this._proposalsShown) return
    const place = (
      path: string,
      wrapper: HTMLElement,
      insert: (view: HTMLElement) => void
    ): void => {
      const proposal = proposals?.[path]
      const existing = [...wrapper.children].find((el) =>
        el.classList.contains('schema-proposal')
      ) as HTMLElement | undefined
      const signature = proposal ? this.proposalSignature(path, proposal) : ''
      if ((existing?.dataset.signature ?? '') === signature) return
      existing?.remove()
      if (!proposal) return
      const view = this.buildProposal(path, proposal, wrapper)
      view.classList.add('schema-proposal')
      view.dataset.proposal = path
      view.dataset.signature = signature
      insert(view)
    }
    for (const [path, entry] of this._index) {
      place(path, entry.wrapper, (view) =>
        entry.wrapper.insertBefore(view, entry.errorSlot)
      )
    }
    for (const [path, host] of this._hosts) {
      place(path, host.wrapper, (view) =>
        host.after ? host.after.after(view) : host.wrapper.prepend(view)
      )
    }
    this._proposalsShown = proposals != null
    // A read-only form shows what is proposed and offers no way to act on it.
    for (const el of this.querySelectorAll('.schema-proposal-actions')) {
      ;(el as HTMLElement).hidden = this.readOnly
    }
    const slot = this._hiddenProposalSlot
    if (!slot) return
    const orphans = Object.keys(proposals ?? {}).filter(
      (path) => !this._index.has(path) && !this._hosts.has(path)
    )
    const text =
      orphans.length === 0
        ? ''
        : `${localize(
            'Proposed changes to fields not shown here'
          )}: ${orphans.join(', ')}`
    if (slot.textContent !== text) slot.textContent = text
    slot.hidden = orphans.length === 0
  }

  private _proposalsShown = false

  /*
  What a rendered proposal depends on. A custom view is also given the current value and
  the read-only state, so it is rebuilt when either changes.
  */
  private proposalSignature(path: string, proposal: FieldProposal): string {
    const parts: unknown[] = [proposal.value, proposal.reason, proposal.source]
    if (this.proposalView) {
      parts.push(this.readOnly, getByPath(this._value, path))
    }
    try {
      return JSON.stringify(parts) ?? String(parts)
    } catch {
      // A value JSON cannot express (a BigInt, a cycle) still has to render.
      return parts.map(String).join('|')
    }
  }

  private buildProposal(
    path: string,
    proposal: FieldProposal,
    wrapper: HTMLElement
  ): HTMLElement {
    // A read-only form does not change its value, whoever drew the buttons.
    const accept = () => {
      if (!this.readOnly) this.acceptProposal(path)
    }
    const reject = () => {
      if (!this.readOnly) this.rejectProposal(path)
    }
    if (this.proposalView) {
      return this.proposalView({
        path,
        label: wrapper.querySelector('label, summary')?.textContent ?? path,
        current: getByPath(this._value, path),
        proposal,
        readOnly: this.readOnly,
        accept,
        reject,
        elements,
      })
    }
    let shown: string
    try {
      shown =
        proposal.value !== null && typeof proposal.value === 'object'
          ? JSON.stringify(proposal.value)
          : String(proposal.value)
    } catch {
      shown = String(proposal.value)
    }
    /*
    Proposals arrive as data, often from another system. Only a string is shown as the
    reason or source: tosijs reads a plain object as element properties, `innerHTML`
    included, so anything else is left out.
    */
    const text = (value: unknown): string | null =>
      typeof value === 'string' && value !== '' ? value : null
    const source = text(proposal.source)
    const reason = text(proposal.reason)
    return div(
      span(
        { class: 'schema-proposal-value' },
        `${localize('Proposed')}: `,
        b(shown)
      ),
      ...(source ? [span({ class: 'schema-proposal-source' }, source)] : []),
      span(
        { class: 'schema-proposal-actions' },
        button(
          { type: 'button', class: 'schema-accept', onClick: accept },
          localize('Accept')
        ),
        button(
          { type: 'button', class: 'schema-reject', onClick: reject },
          localize('Reject')
        )
      ),
      ...(reason ? [span({ class: 'schema-proposal-reason' }, reason)] : [])
    )
  }

  /*
  A curated form can be invalid somewhere it does not show. Saying nothing would let a
  curated view save a value that does not conform, so those errors are listed above the
  fields. An uncurated form shows every property and has no such slot.
  */
  private syncHiddenErrors(): void {
    const slot = this._hiddenErrorSlot
    if (!slot) return
    const hidden = hiddenErrors(this._errors, this._index.keys())
    // Unchanged on most keystrokes, and this is a live region: do not re-announce it.
    const signature = JSON.stringify(hidden)
    if (signature === this._hiddenErrorSignature) return
    this._hiddenErrorSignature = signature
    slot.hidden = hidden.length === 0
    slot.replaceChildren(
      ...(hidden.length === 0
        ? []
        : [
            span(localize('Problems in fields not shown here')),
            ul(
              ...hidden.map((error) =>
                li(
                  error.path ? `${error.path}: ${error.message}` : error.message
                )
              )
            ),
          ])
    )
  }

  content = null

  /** Stops the locale observer. A proxy's `.observe()` returns this, not a `Listener`. */
  private _stopLocale: (() => void) | null = null

  connectedCallback(): void {
    super.connectedCallback()
    live.add(this)
    // The chrome ("Add …", the reorder tooltips) is localized when it is built, so a locale
    // change has to rebuild it — otherwise switching language leaves this form in the old
    // one until something else happens to touch it.
    this._stopLocale = i18n.locale.observe(() => this.rebuild())
  }

  disconnectedCallback(): void {
    live.delete(this)
    /*
    A proxy's `.observe()` hands back the function that stops it. This passed that function to
    `unobserve()`, which wants the `Listener` that the module-level `observe()` returns, so it
    threw "unobserve failed, listener not found" every time a form left the page, and the
    observer was never removed.
    */
    this._stopLocale?.()
    this._stopLocale = null
    super.disconnectedCallback()
  }

  /** Throw away the DOM and build it again — a schema change, or a new plugin. */
  rebuild(): void {
    this._builtFor = null
    this.queueRender()
  }

  /*
  Rebuild ONLY when the schema changed.

  A value change syncs the existing controls instead. That distinction is the difference
  between a form you can type in and one that fights you — and it is why `value` is the
  model rather than a seed for the DOM.
  */
  render(): void {
    super.render()
    /*
    No schema? Infer one from the value — ONCE.

    Re-inferring on every value change would rebuild the form under the user's hands the
    moment they typed something that changed a type, which is the opposite of what this
    component is for. The inferred schema is stored, so `form.schema` returns it and a
    consumer can read it, edit it, and set it back. Set `.schema` explicitly to re-derive.
    */
    const inferSchema = getSchemaValidator()?.inferSchema
    /*
    "No schema" includes one inferred from an EMPTY value. Inferring from the initial `{}` gave
    `{properties: {}}`, which then counted as an answer for good: <tosi-crud>'s New, with no
    schema, handed the form a record with fields and it went on saying "This schema describes
    no properties". An inferred schema with nothing in it is not a schema to keep.
    */
    const nothingKnown =
      !this._schema?.properties ||
      (this._schema.$inferred === true &&
        Object.keys(this._schema.properties).length === 0)
    if (nothingKnown && this._value) {
      if (inferSchema) {
        const inferred = inferSchema(this._value) as JSONSchema
        if (inferred?.properties && Object.keys(inferred.properties).length)
          this._schema = relaxInferred(inferred)
      } else {
        // Nothing to render and no way to work out what to render. Say so where the user is
        // looking, not only in the console.
        warnCannotInfer()
      }
    }
    if (this._builtFor !== this._schema) {
      this._nodes = fieldsFor(this._schema)
      this._entries.clear()
      this._curationWarnings.clear()
      this._hiddenErrorSlot = null
      this._hiddenErrorSignature = ''
      if (this._curation) {
        this._nodes = this.adopt(curateNodes(this._nodes, this._curation))
        this._hiddenErrorSlot = div({
          class: 'schema-hidden-errors',
          role: 'alert',
          hidden: true,
        })
      }
      this._watchPanels = false
      this._hosts.clear()
      this._arrays.clear()
      this._hiddenProposalSlot = div({
        class: 'schema-hidden-proposals',
        hidden: true,
      })
      this._index.clear()
      this.textContent = ''
      /*
      A form with no fields explains itself.

      With no `schema` and no validator to infer one from there is nothing to render, and an
      empty `<form>` looks like a broken component rather than a missing argument. The
      component's own rule everywhere else is that what it cannot render says so — this was
      the one place that rendered nothing instead.
      */
      const empty = this._nodes.length === 0
      this.append(
        formElement(
          { class: 'schema-form', onSubmit: (e: Event) => e.preventDefault() },
          ...(empty
            ? [
                div(
                  { class: 'schema-empty' },
                  this._schema?.properties
                    ? localize('This schema describes no properties.')
                    : schemaValidationAvailable()
                    ? localize('No schema, and nothing to infer one from.')
                    : localize(
                        'No schema — register a validator with inferSchema, or set one.'
                      )
                ),
              ]
            : [
                ...(this._hiddenErrorSlot ? [this._hiddenErrorSlot] : []),
                this._hiddenProposalSlot,
                ...this._nodes.map((n) => this.buildNode(n)),
              ])
        )
      )
      this._builtFor = this._schema
    }
    this.syncArrays()
    this.syncVariants()
    this._fields = this.allFields()
    this.applyReadOnly()
    this.syncValues()
    this.syncPlugins()
    this.refreshErrors()
    this.syncErrors()
    this.syncProposals()
  }
}

/*
Forms currently on screen, so registering a plugin can reach them.

Without this, a plugin registered from application code — after the page has rendered, which
is the normal case — would take effect only on the NEXT form. That asymmetry is what made the
component this design learned from need a load-bearing bare import above its component
definition (its SF-4), and it is a trap because everything looks fine until it doesn't.
*/
const live = new Set<TosiSchemaForm>()
// A validator registered after a form is on screen still takes effect — same reasoning as
// the plugin registry: registration order is not something a consumer should have to think
// about.
onSchemaValidatorChanged(() => {
  for (const form of live) form.rebuild()
})
onFieldPluginsChanged((format) => {
  // Only the forms that actually use the format — a registration is not an excuse to throw
  // away the DOM of every unrelated form on the page.
  for (const form of live) {
    if (schemaUsesFormat(form.schema, format)) form.rebuild()
  }
})

export const tosiSchemaForm =
  TosiSchemaForm.elementCreator() as ElementCreator<TosiSchemaForm>
